import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Text,
    Columns,
    Column,
    Inline,
    Button,
    Badge,
    IconMail,
    IconMobile,
    IconLocation,
    TextField,
    TabsProvider,
    TableCell,
    Dialog,
    Textarea,
    Actions,
    Table,
    TableHeader,
    TableRow,
    TableHeaderCell,
    TableBody,
    Tabs,
    Tab,
    TabPanels,
    TabPanel,
    Drawer,
    Pagination,
    IconUpload,
    IconTick,
    IconDownload,
    IconSearch,
    Dropdown,
    IconDocument,
    IconPrint,
    Stack,
    Card,
    Divider,
} from 'braid-design-system';
import Loading from '../components/Loading';
import GuaranteesTab from '../components/ApproveMobileAppCustomer/CustomerDetailDrawer/GuaranteesTab';
import StandingOrderDrawer from '../components/StandingOrder/StandingOrderDrawer';
import { useToast } from '../hooks/useToast';
import { config } from '../config/app.config';
import apiClient from '../clients/apiClient';
import { CustomerResponse, CustomerSmsNote, PlAccountResponse, addCustomerNote, getCustomerAccounts, getCustomerDetails, getCustomerDocuments, getCustomerSmsNotes, sendCustomerSms, getCustomerNominee, getCustomerInfo } from '../services/approveMobileCustomerService';
import { PlTransactionResponse, getPaginatedTransactionsOfAccount, PaginatedPlTransactions } from '../services/transactionService';
import { useUserPermissionAndMenu } from '../contexts/UserPermissionAndMenuProvider';
import DateField from '../components/DateField';
import TransactionPrintDrawer from '../components/Transactions/TransactionPrintDrawer';
import { uploadCustomerTax, getCustomerTaxInfo } from '../services/approveMobileCustomerService';
import { getCustomerNicPhotos, NicPhotosResponse } from '../services/nicService';
import { getCustomerDebtorAccountsById, getDebtorProductsByAccountId, DebtorProduct } from '../services/customerService';
import { useAuth } from "../contexts/AuthProvider";

const CustomerSearchDrawer = React.lazy(() => import('./ApproveMobileAppCustomer'));

const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString();
};



const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true
    }).format(value);
};

const formatTransactionDate = (dateStr: any) => {
    if (!dateStr) return '-';
    try {
        const str = typeof dateStr === 'string' ? dateStr : new Date(dateStr).toISOString();
        const datePart = str.split('T')[0];
        const parts = datePart.split('-');
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return datePart;
    } catch (e) {
        return '-';
    }
};

const formatInteger = (value: number) => {
    return Math.trunc(value);
};

const getFileUrl = (filePathOrUrl?: string | null) => {
    if (!filePathOrUrl) return null;
    // If it's already a full URL (http/https), use as-is
    if (filePathOrUrl.startsWith('http://') || filePathOrUrl.startsWith('https://')) {
        return filePathOrUrl;
    }
    // If path already contains /api/customer/files/, don't double-prepend
    if (filePathOrUrl.includes('/api/customer/files/')) {
        const base = config.apiUrl.replace(/\/api\/?$/, '');
        // Extract the part after /api/customer/files/
        const parts = filePathOrUrl.split('/api/customer/files/');
        if (parts.length === 2) {
            return `${base}/api/customer/files/${parts[1].replace(/^\//, '')}`;
        }
    }
    // Otherwise, it's a relative path - prepend the API base URL
    const cleanPath = filePathOrUrl.replace(/^uploads\//, '').replace(/^\//, '');
    return `${config.apiUrl.replace(/\/api\/?$/, '')}/api/customer/files/${cleanPath}`;
};

const getTodayDateString = () => {
    return new Date().toISOString().split('T')[0];
};

const getOneMonthAgoDateString = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
};

type SmsNotesTabContentProps = {
    smsNotesFilter: 'all' | 'sms' | 'note';
    setSmsNotesFilter: React.Dispatch<React.SetStateAction<'all' | 'sms' | 'note'>>;
    smsNotesLoading: boolean;
    filteredSmsNotes: CustomerSmsNote[];
    onRefresh: () => Promise<void> | void;
};

const SmsNotesTabContent: React.FC<SmsNotesTabContentProps> = ({
    smsNotesFilter,
    setSmsNotesFilter,
    smsNotesLoading,
    filteredSmsNotes,
    onRefresh
}) => {
    useEffect(() => {
        void onRefresh();
    }, [onRefresh]);

    return (
        <Stack space="medium">
            <Box style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                    <Text size="small" weight="strong" tone="secondary">Filter by Type</Text>
                    <Box style={{ display: 'flex', flexDirection: 'row', gap: '20px', alignItems: 'center' }}>
                        {(['all', 'sms', 'note'] as const).map((type) => {
                            const labelMap = { all: 'All', sms: 'SMS Only', note: 'Notes Only' };
                            const isSelected = smsNotesFilter === type;
                            return (
                                <label
                                    key={type}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="sms-notes-filter"
                                        value={type}
                                        checked={isSelected}
                                        onChange={() => setSmsNotesFilter(type)}
                                        style={{
                                            cursor: 'pointer',
                                            width: '16px',
                                            height: '16px',
                                            accentColor: '#0052cc',
                                        }}
                                    />
                                    <Text size="small">{labelMap[type]}</Text>
                                </label>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
            {smsNotesLoading ? (
                <Text tone="secondary">Loading SMS and notes...</Text>
            ) : (
                <Table label="SMS and notes">
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>
                                <Text size="xsmall">Date</Text>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text size="xsmall">Type</Text>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text size="xsmall">Message</Text>
                            </TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredSmsNotes.length > 0 ? (
                            filteredSmsNotes.map((note) => (
                                <TableRow key={note.id}>
                                    <TableCell>
                                        <Text size="small">{formatDate(note.c_at ?? null)}</Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text size="small">{note.type ?? '-'}</Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text size="small">{note.text ?? '-'}</Text>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colspan={3} align="center">
                                    <Text size="small" tone="secondary">No SMS or notes found.</Text>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            )}
        </Stack>
    );
};


// Other Info Tab Content Component
const OtherInfoTabContent: React.FC<{
    customer: CustomerResponse | null;
    nominee: any;
    customerInfos: any[];
    customerDocs: Array<{ id?: number; document_name?: string | null; file_url?: string | null }>;
    nicPhotos?: NicPhotosResponse | null;
    nomineeLoading: boolean;
    customerInfosLoading: boolean;
    customerDocsLoading: boolean;
    nicPhotosLoading?: boolean;
}> = ({
    customer,
    nominee,
    customerInfos,
    customerDocs,
    nicPhotos,
    nomineeLoading,
    customerInfosLoading,
    customerDocsLoading,
    nicPhotosLoading,
}) => {


    if (!customer) return <Text>No customer data</Text>;

    const nicFrontUrl = getFileUrl(nicPhotos?.front_url || nicPhotos?.front);
    const nicBackUrl = getFileUrl(nicPhotos?.back_url || nicPhotos?.back);

    return (
        <Stack space="large">
            {/* Customer Basic Info Section */}
            <Card>
                <Stack space="medium">
                    <Text weight="strong" size="large">Customer Information</Text>
                    <Divider />
                    <Columns space="large" collapseBelow="tablet">
                        <Column width="1/2">
                            <Stack space="small">
                                <Inline space="small" alignY="center">
                                                    <Text weight="strong" size="small">Title:</Text>
                                                    <Text size="small">{customer.title_ln1 || '-'}</Text>
                                                </Inline>
                                <Inline space="small" alignY="center">
                                                    <Text weight="strong" size="small">Full Name:</Text>
                                                    <Text size="small">{customer.full_name_ln1 || '-'}</Text>
                                                </Inline>
                                <Inline space="small" alignY="center">
                                                    <Text weight="strong" size="small">Branch:</Text>
                                                    <Text size="small">{customer.branch?.name_ln1 || '-'}</Text>
                                                </Inline>
                                {/* <Inline space="small" alignY="center">
                                                    <Text weight="strong" size="small">Date of Birth:</Text>
                                                    <Text size="small">{customer.date_of_birth ? new Date(customer.date_of_birth).toLocaleDateString() : '-'}</Text>
                                                </Inline> */}
                                <Inline space="small" alignY="center">
                                                    <Text weight="strong" size="small">Credit Limit Date:</Text>
                                                    <Text size="small">{customer.credit_limit_date ? new Date(customer.credit_limit_date).toLocaleDateString() : '-'}</Text>
                                                </Inline>
                                <Inline space="small" alignY="center">
                                                    <Text weight="strong" size="small">NIC Front:</Text>
                                                    {nicPhotosLoading ? (
                                                        <Text size="small" tone="secondary">Loading...</Text>
                                                    ) : nicFrontUrl ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="small"
                                                            onClick={() => window.open(nicFrontUrl, '_blank', 'noopener,noreferrer')}
                                                        >
                                                            <IconDocument /> View
                                                        </Button>
                                                    ) : (
                                                        <Text size="small" tone="secondary">Unavailable</Text>
                                                    )}
                                                </Inline>
                                            </Stack>
                        </Column>
                        <Column width="1/2">
                            <Stack space="small">
                                <Inline space="small" alignY="center">
                                                    <Text weight="strong" size="small">SMS Status:</Text>
                                                    <Badge tone={customer.sms_status === 1 ? 'positive' : 'caution'}>
                                                        {customer.sms_status === 1 ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </Inline>
                                <Inline space="small" alignY="center">
                                                    <Text weight="strong" size="small">POS App Status:</Text>
                                                    <Badge tone={customer.pos_app_status === 1 ? 'positive' : 'caution'}>
                                                        {customer.pos_app_status === 1 ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </Inline>
                                <Inline space="small" alignY="center">
                                                    <Text weight="strong" size="small">Credit Limit:</Text>
                                                    <Text size="small">{customer.credit_limit ? formatCurrency(customer.credit_limit) : '-'}</Text>
                                                </Inline>
                                <Inline space="small" alignY="center">
                                                    <Text weight="strong" size="small">Member Date:</Text>
                                                    <Text size="small">{customer.member_date ? new Date(customer.member_date).toLocaleDateString() : '-'}</Text>
                                                </Inline>
                                <Inline space="small" alignY="center">
                                                    <Text weight="strong" size="small">NIC Back:</Text>
                                                    {nicPhotosLoading ? (
                                                        <Text size="small" tone="secondary">Loading...</Text>
                                                    ) : nicBackUrl ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="small"
                                                            onClick={() => window.open(nicBackUrl, '_blank', 'noopener,noreferrer')}
                                                        >
                                                            <IconDocument /> View
                                                        </Button>
                                                    ) : (
                                                        <Text size="small" tone="secondary">Unavailable</Text>
                                                    )}
                                                </Inline>
                                            </Stack>
                        </Column>
                    </Columns>
                </Stack>
            </Card>

            {/* Nominee Details Section */}
            <Card>
                <Stack space="medium">
                    <Text weight="strong" size="large">Nominee Details</Text>
                    <Divider />
                    {nomineeLoading ? (
                        <Text tone="secondary">Loading nominee details...</Text>
                    ) : nominee ? (
                        <Stack space="small">
                            <Inline space="small" alignY="center">
                                <Text weight="strong" size="small">Name:</Text>
                                <Text size="small">{nominee.name_ln1 || '-'}</Text>
                            </Inline>
                            <Inline space="small" alignY="center">
                                <Text weight="strong" size="small">Date of Birth:</Text>
                                <Text size="small">{nominee.date_of_birth ? new Date(nominee.date_of_birth).toLocaleDateString() : '-'}</Text>
                            </Inline>
                            <Inline space="small" alignY="center">
                                <Text weight="strong" size="small">Relationship:</Text>
                                <Text size="small">{nominee.relationship || '-'}</Text>
                            </Inline>
                            <Inline space="small" alignY="center">
                                <Text weight="strong" size="small">Status:</Text>
                                <Badge tone={nominee.status === 1 ? 'positive' : 'caution'}>
                                    {nominee.status === 1 ? 'Active' : 'Inactive'}
                                </Badge>
                            </Inline>
                        </Stack>
                    ) : (
                        <Text tone="secondary">No nominee information available</Text>
                    )}
                </Stack>
            </Card>

            {/* Customer Other Information Section */}
            <Card>
                <Stack space="medium">
                    <Text weight="strong" size="large">Customer Other Information</Text>
                    <Divider />
                    {customerInfosLoading ? (
                        <Text tone="secondary">Loading customer information...</Text>
                    ) : customerInfos.length > 0 ? (
                        <Table label="Customer Information">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell><Text size="xsmall" weight="strong">Field Name</Text></TableHeaderCell>
                                    <TableHeaderCell><Text size="xsmall" weight="strong">Value</Text></TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {customerInfos.map((info, index) => (
                                    <TableRow key={info.id ?? index}>
                                        <TableCell>
                                            <Text size="xsmall">{info.field_name || '-'}</Text>
                                        </TableCell>
                                        <TableCell>
                                            <Text size="small">{info.value || '-'}</Text>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Text tone="secondary">No additional information available</Text>
                    )}
                </Stack>
            </Card>

            {/* Customer Documents Section */}
            <Card>
                <Stack space="medium">
                    <Text weight="strong" size="large">Customer Documents</Text>
                    <Divider />
                    {customerDocsLoading ? (
                        <Text tone="secondary">Loading documents...</Text>
                    ) : customerDocs.length > 0 ? (
                        <Table label="Customer Documents">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell><Text size="xsmall" weight="strong">Document Name</Text></TableHeaderCell>
                                    <TableHeaderCell><Text size="xsmall" weight="strong">Action</Text></TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {customerDocs.map((doc, index) => (
                                    <TableRow key={doc.id ?? index}>
                                        <TableCell>
                                            <Text size="small">{doc.document_name || 'Document'}</Text>
                                        </TableCell>
                                        <TableCell>
                                            {doc.file_url ? (
                                                <Button
                                                    variant="ghost"
                                                    size="small"
                                                    onClick={() => window.open(getFileUrl(doc.file_url) ?? '', '_blank', 'noopener,noreferrer')}
                                                >
                                                    <IconDocument /> View Document
                                                </Button>
                                            ) : (
                                                <Text size="small" tone="secondary">Unavailable</Text>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Text tone="secondary">No documents available</Text>
                    )}
                </Stack>
            </Card>
        </Stack>
    );
};

const CustomerProfile: React.FC = () => {
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);
    const { customerNumber } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [customerSearchDrawerOpen, setCustomerSearchDrawerOpen] = useState(false);
    const { hasPermission } = useUserPermissionAndMenu();
    const [customer, setCustomer] = useState<CustomerResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [accounts, setAccounts] = useState<PlAccountResponse[]>([]);
    const [dormantAccounts, setDormantAccounts] = useState<PlAccountResponse[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<PlAccountResponse | null>(null);
    const [transactionsOpen, setTransactionsOpen] = useState(false);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactions, setTransactions] = useState<PlTransactionResponse[]>([]);
    const [transactionsPage, setTransactionsPage] = useState(1);
    const [transactionsTotalPages, setTransactionsTotalPages] = useState(1);
    const [transactionsTotalCount, setTransactionsTotalCount] = useState(0);
    const TRANSACTIONS_PAGE_SIZE = 10;
    const [messageDialogOpen, setMessageDialogOpen] = useState(false);
    const [messageDialogMode, setMessageDialogMode] = useState<'sms' | 'note'>('sms');
    const [messageText, setMessageText] = useState('');
    const [messageError, setMessageError] = useState(false);
    const [messageSubmitting, setMessageSubmitting] = useState(false);
    const [smsNotes, setSmsNotes] = useState<CustomerSmsNote[]>([]);
    const [smsNotesLoading, setSmsNotesLoading] = useState(false);
    const [smsNotesFilter, setSmsNotesFilter] = useState<'all' | 'sms' | 'note'>('all');
    const [customerDocs, setCustomerDocs] = useState<Array<{ id?: number; document_name?: string | null; file_url?: string | null }>>([]);
    const [customerDocsLoading, setCustomerDocsLoading] = useState(false);
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [standingOrderDrawerOpen, setStandingOrderDrawerOpen] = useState(false);
    const [selectedAccountForStandingOrder, setSelectedAccountForStandingOrder] = useState<PlAccountResponse | null>(null);
    const { user } = useAuth();

    // Search states for account tables
    const [loanSearch, setLoanSearch] = useState("");
    const [savingsSearch, setSavingsSearch] = useState("");
    const [fdSearch, setFdSearch] = useState("");
    // Status filter states: 'active' | 'inactive' | 'applications' | 'dormant' | 'all'
    const [loanStatusFilter, setLoanStatusFilter] = useState<string>('active');
    const [savingsStatusFilter, setSavingsStatusFilter] = useState<string>('active');
    const [fdStatusFilter, setFdStatusFilter] = useState<string>('active');
    // Debtors tab data (pl_account_category_id = 6); balance > 0 = Active, balance = 0 = Inactive
    const [debtorAccounts, setDebtorAccounts] = useState<PlAccountResponse[]>([]);
    const [debtorAccountsLoading, setDebtorAccountsLoading] = useState(false);
    const [debtorSearch, setDebtorSearch] = useState("");
    const [debtorStatusFilter, setDebtorStatusFilter] = useState<string>('all');
    // Debtor products drawer
    const [productsDrawerOpen, setProductsDrawerOpen] = useState(false);
    const [productsAccount, setProductsAccount] = useState<PlAccountResponse | null>(null);
    const [products, setProducts] = useState<DebtorProduct[]>([]);
    const [productsLoading, setProductsLoading] = useState(false);

    // Other Info tab data
    const [nominee, setNominee] = useState<any>(null);
    const [customerInfos, setCustomerInfos] = useState<any[]>([]);
    const [nicPhotos, setNicPhotos] = useState<NicPhotosResponse | null>(null);
    const [nomineeLoading, setNomineeLoading] = useState(false);
    const [customerInfosLoading, setCustomerInfosLoading] = useState(false);
    const [nicPhotosLoading, setNicPhotosLoading] = useState(false);

    // Tax status dialog state
    const [taxDialogOpen, setTaxDialogOpen] = useState(false);
    const [taxFile, setTaxFile] = useState<File | null>(null);
    const [taxTin, setTaxTin] = useState('');
    const [taxValidUntil, setTaxValidUntil] = useState('');
    const [taxSubmitting, setTaxSubmitting] = useState(false);
    const [taxLatter, setTaxLatter] = useState('');
    const [taxEditable, setTaxEditable] = useState(true);
    const [taxHasStatus, setTaxHasStatus] = useState(false);
    const [taxExistingFile, setTaxExistingFile] = useState<string | null>(null);

    // Transaction Statement states
    const [statementDrawerOpen, setStatementDrawerOpen] = useState(false);
    const [statementFromDate, setStatementFromDate] = useState(getOneMonthAgoDateString());
    const [statementToDate, setStatementToDate] = useState(getTodayDateString());
    const [statementPlAccountId, setStatementPlAccountId] = useState<string>('');
    const [statementLanguage, setStatementLanguage] = useState<'english' | 'sinhala' | 'tamil'>('sinhala');
    const [statementReportType, setStatementReportType] = useState<'single' | 'all_savings' | 'all_loans'>('single');
    const [statementData, setStatementData] = useState<any>(null);
    const [statementLoading, setStatementLoading] = useState(false);

    // QR card dialog state
    const [qrDialogOpen, setQrDialogOpen] = useState(false);
    const [qrCardNumber, setQrCardNumber] = useState('');
    const [printTxId, setPrintTxId] = useState<number | null>(null);

    useEffect(() => {
        let isMounted = true;

        const fetchCustomer = async () => {
            if (!customerNumber) return;
            setLoading(true);
            try {
                const data = await getCustomerDetails(customerNumber);
                if (isMounted) {
                    setCustomer(data);
                    try {
                        if (data?.id) {
                            const taxResp = await getCustomerTaxInfo(data.id);
                            setTaxHasStatus(!!taxResp.has_status);
                            setTaxEditable(!!taxResp.editable);
                            if (taxResp.tax_info) {
                                setTaxTin(taxResp.tax_info.tin_number || '');
                                setTaxValidUntil(taxResp.tax_info.valid_until ? String(taxResp.tax_info.valid_until).split('T')[0] : '');
                                setTaxExistingFile(taxResp.tax_info.file || null);
                            } else {
                                setTaxExistingFile(null);
                            }
                        }
                    } catch (err) {
                        // non-fatal: ignore tax fetch errors on initial load
                    }
                }
            } catch (error: any) {
                if (isMounted) {
                    toast.showError(error?.message ?? 'Failed to load customer details');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchCustomer();

        return () => {
            isMounted = false;
        };
    }, [customerNumber, toast]);

    useEffect(() => {
        let isMounted = true;

        const fetchAccounts = async () => {
            if (!customer?.id) return;
            setAccountsLoading(true);
            try {
                const data = await getCustomerAccounts(customer.id, undefined, false, true);
                if (isMounted) {
                    setAccounts(data || []);
                }
            } catch (error: any) {
                if (isMounted) {
                    toast.showError(error?.message ?? 'Failed to load customer accounts');
                }
            } finally {
                if (isMounted) {
                    setAccountsLoading(false);
                }
            }
        };

        fetchAccounts();

        return () => {
            isMounted = false;
        };
    }, [customer?.id, toast]);

    useEffect(() => {
        let isMounted = true;

        const fetchDebtorAccounts = async () => {
            if (!customer?.id) return;
            setDebtorAccountsLoading(true);
            try {
                const data = await getCustomerDebtorAccountsById(customer.id);
                if (isMounted) {
                    setDebtorAccounts(data || []);
                }
            } catch (error: any) {
                if (isMounted) {
                    setDebtorAccounts([]);
                }
            } finally {
                if (isMounted) {
                    setDebtorAccountsLoading(false);
                }
            }
        };

        fetchDebtorAccounts();

        return () => {
            isMounted = false;
        };
    }, [customer?.id]);

    // Fetch dormant accounts when checkbox is toggled
    const fetchDormantAccounts = useCallback(async () => {
        if (!customer?.id) return;
        try {
            const data = await getCustomerAccounts(customer.id, undefined, true);
            setDormantAccounts(data || []);
        } catch (error: any) {
            // ignore errors for dormant fetch
        }
    }, [customer?.id]);

    useEffect(() => {
        if (savingsStatusFilter === 'dormant' || savingsStatusFilter === 'all') {
            fetchDormantAccounts();
        } else {
            setDormantAccounts([]);
        }
    }, [savingsStatusFilter, fetchDormantAccounts]);
    // Fetch nominee data
    const fetchNominee = useCallback(async (customerId?: number) => {
        if (!customerId) return;
        setNomineeLoading(true);
        try {
            const data = await getCustomerNominee(customerId);
            if (isMountedRef.current) {
                setNominee(data);
            }
        } catch (error: any) {
            // Silently fail
        } finally {
            if (isMountedRef.current) {
                setNomineeLoading(false);
            }
        }
    }, []);

    // Fetch customer infos
    const fetchCustomerInfos = useCallback(async (customerId?: number) => {
        if (!customerId) return;
        setCustomerInfosLoading(true);
        try {
            const data = await getCustomerInfo(customerId);
            if (isMountedRef.current) {
                setCustomerInfos(data || []);
            }
        } catch (error: any) {
            // Silently fail
        } finally {
            if (isMountedRef.current) {
                setCustomerInfosLoading(false);
            }
        }
    }, []);

    // Fetch customer documents
    const fetchCustomerDocs = useCallback(async (customerId?: number) => {
        if (!customerId) return;
        setCustomerDocsLoading(true);
        try {
            const data = await getCustomerDocuments(customerId);
            if (isMountedRef.current) {
                setCustomerDocs(data || []);
            }
        } catch (error: any) {
            // Silently fail
        } finally {
            if (isMountedRef.current) {
                setCustomerDocsLoading(false);
            }
        }
    }, []);

    // Fetch customer NIC photos
    const fetchNicPhotos = useCallback(async (customerId?: number) => {
        if (!customerId) return;
        setNicPhotosLoading(true);
        try {
            const data = await getCustomerNicPhotos(customerId);
            if (isMountedRef.current) {
                setNicPhotos(data || null);
            }
        } catch (error: any) {
            // Silently fail
        } finally {
            if (isMountedRef.current) {
                setNicPhotosLoading(false);
            }
        }
    }, []);

    // Fetch other info data when customer is loaded
    useEffect(() => {
        if (!customer?.id) return;
        fetchNominee(customer.id);
        fetchCustomerInfos(customer.id);
        fetchCustomerDocs(customer.id);
        fetchNicPhotos(customer.id);
    }, [customer?.id, fetchNominee, fetchCustomerInfos, fetchCustomerDocs, fetchNicPhotos]);

    const [photoError, setPhotoError] = useState(false);
    const [sigError, setSigError] = useState(false);

    useEffect(() => {
        setPhotoError(false);
        setSigError(false);
    }, [customer?.customer_number]);

    const photoSrc = customer?.customer_number && !photoError
        ? `${config.apiUrl}/customer/${customer.customer_number}/photo`
        : undefined;

    const signatureSrc = customer?.customer_number && !sigError
        ? `${config.apiUrl}/customer/${customer.customer_number}/signature`
        : undefined;

    const totals = useMemo(() => {
        const savingAccounts = accounts.filter(
            (account) => account.account_type?.pl_account_category_id === 1
        );
        const loanAccounts = accounts.filter(
            (account) => account.account_type?.pl_account_category_id === 2
        );
        const fdAccounts = accounts.filter(
            (account) =>
                account.account_type?.pl_account_category_id === 1 &&
                [15, 16, 17].includes(account.account_type?.interest_policy ?? -1)
        );

        const totalSavings = savingAccounts.reduce(
            (sum, account) => sum + (account.balance ?? 0),
            0
        );
        const totalLoans = loanAccounts.reduce(
            (sum, account) => sum + (account.balance ?? 0),
            0
        );
        const totalPastDue = loanAccounts.reduce(
            (sum, account) => sum + (account.past_due_amount ?? 0),
            0
        );
        const totalFd = fdAccounts.reduce(
            (sum, account) => sum + (account.balance ?? 0),
            0
        );

        const savingsPercent = totalLoans > 0 ? (totalSavings / totalLoans) * 100 : 0;
        const loanPercent = totalSavings > 0 ? (totalLoans / totalSavings) * 100 : 0;
        const pastDuePercent = totalLoans > 0 ? (totalPastDue / totalLoans) * 100 : 0;

        return {
            totalSavings,
            totalLoans,
            totalPastDue,
            totalFd,
            totalShares: 0,
            savingsPercent,
            loanPercent,
            pastDuePercent
        };
    }, [accounts]);

    const savingAccounts = useMemo(
        () => accounts.filter((account) => account.account_type?.pl_account_category_id === 1),
        [accounts]
    );
    const loanAccounts = useMemo(
        () => accounts.filter((account) => account.account_type?.pl_account_category_id === 2),
        [accounts]
    );
    const fdAccounts = useMemo(
        () => savingAccounts.filter((account) => [15, 16, 17].includes(account.account_type?.interest_policy ?? -1)),
        [savingAccounts]
    );
    const savingsExcludingFd = useMemo(
        () => savingAccounts.filter((account) => ![15, 16, 17].includes(account.account_type?.interest_policy ?? -1)),
        [savingAccounts]
    );
    const canViewAccountInfo = useMemo(
        () => hasPermission('CUSTOMER:ACCOUNT_INFO'),
        [hasPermission]
    );

    const allPlAccounts = useMemo(() => {
        return [...accounts, ...dormantAccounts];
    }, [accounts, dormantAccounts]);

    const savingsActiveAccounts = useMemo(() => {
        return allPlAccounts.filter(acc => acc.account_type?.pl_account_category_id === 1 && (acc.balance ?? 0) !== 0);
    }, [allPlAccounts]);

    const loanActiveAccounts = useMemo(() => {
        return allPlAccounts.filter(acc => acc.account_type?.pl_account_category_id === 2 && (acc.balance ?? 0) !== 0);
    }, [allPlAccounts]);

    const savingsClosedAccounts = useMemo(() => {
        return allPlAccounts.filter(acc => acc.account_type?.pl_account_category_id === 1 && (acc.balance ?? 0) === 0);
    }, [allPlAccounts]);

    const loanClosedAccounts = useMemo(() => {
        return allPlAccounts.filter(acc => acc.account_type?.pl_account_category_id === 2 && (acc.balance ?? 0) === 0);
    }, [allPlAccounts]);

    const getVal = (val1: any, val2: any) => {
        if (val1 &&
            String(val1).trim() !== '' &&
            String(val1).toLowerCase() !== 'none' &&
            String(val1).toLowerCase() !== 'null' &&
            String(val1).toLowerCase() !== 'undefined') {
            return val1;
        }
        return val2 || '';
    };

    const handleFetchStatement = async () => {
        if (!customer) {
            toast.showError('Customer details not loaded');
            return;
        }
        if (statementReportType === 'single' && !statementPlAccountId) {
            toast.showError('Please select a PL Account');
            return;
        }
        setStatementLoading(true);
        try {
            let url = `/customer/accounts/${statementPlAccountId}/statement`;
            if (statementReportType === 'all_savings') {
                url = `/customer/${customer.id}/statement/category/1`;
            } else if (statementReportType === 'all_loans') {
                url = `/customer/${customer.id}/statement/category/2`;
            }
            const response = await apiClient.get(url, {
                params: {
                    from_date: statementFromDate,
                    to_date: statementToDate
                }
            });
            if (response.data && response.data.data) {
                setStatementData(response.data.data);
            } else {
                setStatementData(null);
            }
        } catch (error: any) {
            toast.showError(error?.message ?? 'Failed to fetch statement details');
            setStatementData(null);
        } finally {
            setStatementLoading(false);
        }
    };

    const handlePrintStatement = () => {
        if (!statementData) return;

        let iframe = document.getElementById('statement-print-iframe') as HTMLIFrameElement;
        if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.id = 'statement-print-iframe';
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = 'none';
            document.body.appendChild(iframe);
        }

        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;

        let title = '';
        let fromToText = '';
        let memberNoLabel = '';
        let nameLabel = '';
        let addressLabel = '';
        let accountNoLabel = '';
        let transNoHeader = '';
        let dateHeader = '';
        let debitHeader = '';
        let creditHeader = '';
        let interestHeader = '';
        let otherHeader = '';
        let balanceHeader = '';
        let openingBalanceLabel = '';

        const instName = statementLanguage === 'english' ? statementData.institute_name_ln1 : statementLanguage === 'sinhala' ? getVal(statementData.institute_name_ln2, statementData.institute_name_ln1) : getVal(statementData.institute_name_ln3, statementData.institute_name_ln1);
        const branchName = statementLanguage === 'english' ? statementData.branch_name_ln1 : statementLanguage === 'sinhala' ? getVal(statementData.branch_name_ln2, statementData.branch_name_ln1) : getVal(statementData.branch_name_ln3, statementData.branch_name_ln1);
        const custName = statementLanguage === 'english' ? statementData.customer_name_ln1 : statementLanguage === 'sinhala' ? getVal(statementData.customer_name_ln2, statementData.customer_name_ln1) : getVal(statementData.customer_name_ln3, statementData.customer_name_ln1);
        const custAddress = statementLanguage === 'english' ? statementData.address_ln1 : statementLanguage === 'sinhala' ? getVal(statementData.address_ln2, statementData.address_ln1) : getVal(statementData.address_ln3, statementData.address_ln1);
        const accType = statementLanguage === 'english' ? statementData.account_type_ln1 : statementLanguage === 'sinhala' ? getVal(statementData.account_type_ln2, statementData.account_type_ln1) : getVal(statementData.account_type_ln3, statementData.account_type_ln1);

        if (statementLanguage === 'english') {
            title = `${instName || ''} - Branch: ${branchName || ''}`;
            title = title + ` - print`;
            const catText = statementData.account_category_id === 2 ? 'Loan' : 'Deposit';
            fromToText = `${catText} Transactions from ${statementFromDate} to ${statementToDate}`;
            memberNoLabel = 'Member No';
            nameLabel = "Customer Name";
            addressLabel = "Customer Address";
            accountNoLabel = "Account Number";
            transNoHeader = "Transaction No";
            dateHeader = "Date";
            debitHeader = "Debit";
            creditHeader = "Credit";
            interestHeader = "Interest";
            otherHeader = "Other";
            balanceHeader = "Balance";
            openingBalanceLabel = "Opening Balance";
        } else if (statementLanguage === 'sinhala') {
            title = `${instName || ''} - ශාඛාව : ${branchName || ''}`;
            const catText = statementData.account_category_id === 2 ? 'ණය' : 'තැන්පත්';
            fromToText = `${statementFromDate} සිට ${statementToDate} දින දක්වා ${catText} ගනුදෙනු`;
            memberNoLabel = 'සාමා. අංකය';
            nameLabel = "ගනුදෙනුකරුගේ නම";
            addressLabel = "ගනුදෙනුකරුගේ ලිපිනය";
            accountNoLabel = "ගිණුම් අංකය";
            transNoHeader = "ගනුදෙනු අංකය";
            dateHeader = "දිනය";
            debitHeader = "හර";
            creditHeader = "බැර";
            interestHeader = "පොලිය";
            otherHeader = "වෙනත්";
            balanceHeader = "ශේෂය";
            openingBalanceLabel = "ආරම්භක ශේෂය";
        } else {
            title = `${instName || ''} - கிளை : ${branchName || ''}`;
            const catText = statementData.account_category_id === 2 ? 'கடன்' : 'சேமிப்பு';
            fromToText = `${statementFromDate} முதல் ${statementToDate} வரையிலான ${catText} பரிவர்த்தனைகள்`;
            memberNoLabel = 'உறுப்பினர் எண்';
            nameLabel = "வாடிக்கையாளர் பெயர்";
            addressLabel = "வாடிக்கையாளர் முகவரி";
            accountNoLabel = "கணக்கு எண்";
            transNoHeader = "பரிவர்த்தனை எண்";
            dateHeader = "தேதி";
            debitHeader = "பற்று";
            creditHeader = "வரவு";
            interestHeader = "வட்டி";
            otherHeader = "இதர";
            balanceHeader = "மீதி";
            openingBalanceLabel = "ஆரம்ப இருப்பு";
        }

        const isLoan = statementData.account_category_id === 2;

        const subtitleText = statementData.accounts
            ? (statementLanguage === 'english'
                ? `${statementData.account_category_id === 2 ? 'All Loan' : 'All Savings'} Accounts`
                : statementLanguage === 'sinhala'
                    ? `සියලුම ${statementData.account_category_id === 2 ? 'ණය' : 'තැන්පත්'} ගිණුම්`
                    : `அனைத்து ${statementData.account_category_id === 2 ? 'கடன்' : 'சேமிப்பு'} கணக்குகள்`)
            : accType;

        const accountNoRow = statementData.accounts
            ? ''
            : `<tr>
                <td class="meta-label">${accountNoLabel}</td>
                <td class="meta-value">- ${statementData.account_number || ''}</td>
               </tr>`;

        let bodyHtml = '';
        if (statementData.accounts && Array.isArray(statementData.accounts)) {
            const accountsToPrint = statementData.accounts.filter((acc: any) => {
                const hasTransactions = acc.transactions && acc.transactions.length > 0;
                const isActive = (acc.balance ?? 0) !== 0;
                return isActive || hasTransactions;
            }).sort((a: any, b: any) => {
                const aHasTx = a.transactions && a.transactions.length > 0;
                const bHasTx = b.transactions && b.transactions.length > 0;
                if (aHasTx && !bHasTx) return -1;
                if (!aHasTx && bHasTx) return 1;
                return 0;
            });
            bodyHtml = accountsToPrint.map((acc: any) => {
                const accName = statementLanguage === 'english'
                    ? acc.account_type_ln1
                    : statementLanguage === 'sinhala'
                        ? getVal(acc.account_type_ln2, acc.account_type_ln1)
                        : getVal(acc.account_type_ln3, acc.account_type_ln1);

                const rowsHtml = acc.transactions.map((t: any) => `
                    <tr>
                        <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; font-family: monospace;">${t.transaction_number || '-'}</td>
                        <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc;">${formatTransactionDate(t.entered_date)}</td>
                        <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">${formatCurrency(t.debit)}</td>
                        <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">${formatCurrency(t.credit)}</td>
                        ${isLoan ? `
                            <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">${formatCurrency(t.interest)}</td>
                            <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">${formatCurrency(t.other)}</td>
                        ` : ''}
                        <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right; font-weight: bold;">${formatCurrency(t.balance)}</td>
                    </tr>
                `).join('');

                return `
                    <div style="margin-top: 30px; margin-bottom: 10px;">
                        <div style="font-size: 15px; font-weight: bold; padding: 6px; background-color: #f1f5f9; border-left: 4px solid #0f172a; margin-bottom: 10px;">
                            ${acc.ref_account_number} - ${accName}
                        </div>
                        <table class="trans-table">
                            <thead>
                                <tr>
                                    <th style="width: 15%;">${transNoHeader}</th>
                                    <th style="width: 15%;">${dateHeader}</th>
                                    <th style="width: 15%; text-align: right;">${debitHeader}</th>
                                    <th style="width: 15%; text-align: right;">${creditHeader}</th>
                                    ${isLoan ? `
                                        <th style="width: 12%; text-align: right;">${interestHeader}</th>
                                        <th style="width: 12%; text-align: right;">${otherHeader}</th>
                                    ` : ''}
                                    <th style="width: 15%; text-align: right;">${balanceHeader}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style="font-style: italic;">
                                    <td colspan="2" style="padding: 8px 4px; border-bottom: 1px dotted #ccc; font-weight: bold;">${openingBalanceLabel}</td>
                                    <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">0.00</td>
                                    <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">0.00</td>
                                    ${isLoan ? `
                                        <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">0.00</td>
                                        <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">0.00</td>
                                    ` : ''}
                                    <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right; font-weight: bold;">${formatCurrency(acc.opening_balance)}</td>
                                </tr>
                                ${rowsHtml}
                                <tr style="font-weight: bold; border-top: 1.5px solid #000; border-bottom: 2px double #000;">
                                    <td colspan="2" style="padding: 8px 4px; font-weight: bold;">Total</td>
                                    <td style="padding: 8px 4px; text-align: right;">${formatCurrency(acc.transactions.reduce((sum: number, t: any) => sum + (t.debit || 0), 0))}</td>
                                    <td style="padding: 8px 4px; text-align: right;">${formatCurrency(acc.transactions.reduce((sum: number, t: any) => sum + (t.credit || 0), 0))}</td>
                                    ${isLoan ? `
                                        <td style="padding: 8px 4px; text-align: right;"></td>
                                        <td style="padding: 8px 4px; text-align: right;"></td>
                                    ` : ''}
                                    <td style="padding: 8px 4px; text-align: right;"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                `;
            }).join('');
        } else {
            const rowsHtml = statementData.transactions.map((t: any) => `
                <tr>
                    <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; font-family: monospace;">${t.transaction_number || '-'}</td>
                    <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc;">${formatTransactionDate(t.entered_date)}</td>
                    <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">${formatCurrency(t.debit)}</td>
                    <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">${formatCurrency(t.credit)}</td>
                    ${isLoan ? `
                        <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">${formatCurrency(t.interest)}</td>
                        <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">${formatCurrency(t.other)}</td>
                    ` : ''}
                    <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right; font-weight: bold;">${formatCurrency(t.balance)}</td>
                </tr>
            `).join('');

            bodyHtml = `
                <table class="trans-table">
                    <thead>
                        <tr>
                            <th style="width: 15%;">${transNoHeader}</th>
                            <th style="width: 15%;">${dateHeader}</th>
                            <th style="width: 15%; text-align: right;">${debitHeader}</th>
                            <th style="width: 15%; text-align: right;">${creditHeader}</th>
                            ${isLoan ? `
                                <th style="width: 12%; text-align: right;">${interestHeader}</th>
                                <th style="width: 12%; text-align: right;">${otherHeader}</th>
                            ` : ''}
                            <th style="width: 15%; text-align: right;">${balanceHeader}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr style="font-style: italic;">
                            <td colspan="2" style="padding: 8px 4px; border-bottom: 1px dotted #ccc; font-weight: bold;">${openingBalanceLabel}</td>
                            <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">0.00</td>
                            <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">0.00</td>
                            ${isLoan ? `
                                <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">0.00</td>
                                <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right;">0.00</td>
                            ` : ''}
                            <td style="padding: 8px 4px; border-bottom: 1px dotted #ccc; text-align: right; font-weight: bold;">${formatCurrency(statementData.opening_balance)}</td>
                        </tr>
                        ${rowsHtml}
                        <tr style="font-weight: bold; border-top: 1.5px solid #000; border-bottom: 2px double #000;">
                            <td colspan="2" style="padding: 8px 4px; font-weight: bold;">Total</td>
                            <td style="padding: 8px 4px; text-align: right;">${formatCurrency(statementData.transactions.reduce((sum: number, t: any) => sum + (t.debit || 0), 0))}</td>
                            <td style="padding: 8px 4px; text-align: right;">${formatCurrency(statementData.transactions.reduce((sum: number, t: any) => sum + (t.credit || 0), 0))}</td>
                            ${isLoan ? `
                                <td style="padding: 8px 4px; text-align: right;"></td>
                                <td style="padding: 8px 4px; text-align: right;"></td>
                            ` : ''}
                            <td style="padding: 8px 4px; text-align: right;"></td>
                        </tr>
                    </tbody>
                </table>
            `;
        }

        doc.open();
        doc.write(`
            <html>
            <head>
                <title>Statement Print</title>
                <style>
                    * {
                        text-decoration: none !important;
                        text-decoration-line: none !important;
                        font-family: Arial, sans-serif !important;
                    }
                    @page {
                        margin-right: 30px; !important;
                        margin-left: 50px; !important;
                        margin-top: 50px;
                        margin-bottom: 25px;
                        @bottom-right {
                            content: "Page " counter(page);
                            font-size: 11px;
                            font-family: Arial, sans-serif;
                            color: #555;
                        }
                    }
                    @page :first {
                        margin-top: 50px;
                    }
                    body {
                        margin: 0;
                        color: #333;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 20px;
                    }
                    .title {
                        font-size: 18px;
                        font-weight: bold;
                        margin-bottom: 5px;
                    }
                    .subtitle {
                        font-size: 14px;
                        font-weight: bold;
                        margin-bottom: 15px;
                    }
                    .meta-table {
                        width: 100%;
                        margin-bottom: 20px;
                        border-collapse: collapse;
                    }
                    .meta-table td {
                        padding: 4px 0;
                        font-size: 14px;
                    }
                    .meta-label {
                        width: 150px;
                    }
                    .meta-value {
                        font-weight: bold;
                    }
                    .trans-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 10px;
                    }
                    .trans-table th {
                        border-bottom: 2px solid #000;
                        border-top: 2px solid #000;
                        padding: 8px 4px;
                        font-size: 13px;
                        text-align: left;
                    }
                    .trans-table td {
                        font-size: 13px;
                        padding: 12px 6px !important;
                        border-bottom: 1px solid #e2e8f0 !important;
                        line-height: 1.5 !important;
                    }
                    .divider {
                        border-bottom: 2px solid #000;
                        margin-top: 15px;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="title">${title}</div>
                    <div class="subtitle">${fromToText} (${subtitleText})</div>
                </div>
                <table class="meta-table">
                    <tr>
                        <td class="meta-label">${memberNoLabel}</td>
                        <td class="meta-value">- ${statementData.customer_number || ''}</td>
                    </tr>
                    <tr>
                        <td class="meta-label">${nameLabel}</td>
                        <td class="meta-value">- ${custName || ''}</td>
                    </tr>
                    <tr>
                        <td class="meta-label">${addressLabel}</td>
                        <td class="meta-value">- ${custAddress || ''}</td>
                    </tr>
                    ${accountNoRow}
                </table>
                
                ${bodyHtml}
                <div class="divider"></div>
                <div style="margin-top: 60px; display: flex; justify-content: space-between; align-items: flex-end;">
                    <div style="text-align: center; min-width: 200px;">
                        <div style="border-top: 1px solid #000; padding-top: 8px; font-size: 13px;">
                            ${statementLanguage === 'english' ? 'Branch Manager Signature' : statementLanguage === 'sinhala' ? 'ශාඛා කළමනාකරුගේ අත්සන' : 'கிளை மேலாளர் கையொப்பம்'}
                        </div>
                    </div>
                </div>
            </body>
            </html>
        `);
        doc.close();

        setTimeout(() => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
        }, 300);
    };

    const accountMap = useMemo(() => {
        const map = new Map<number, PlAccountResponse>();
        [...accounts, ...dormantAccounts].forEach((acc) => {
            if (acc.id != null) map.set(acc.id, acc);
        });
        return map;
    }, [accounts, dormantAccounts]);

    const loadTransactions = useCallback(async (plAccountId: number, page: number) => {
        setTransactionsLoading(true);
        try {
            const result: PaginatedPlTransactions = await getPaginatedTransactionsOfAccount(
                plAccountId, page, TRANSACTIONS_PAGE_SIZE
            );
            setTransactions(result.items);
            setTransactionsPage(result.current_page);
            setTransactionsTotalPages(result.total_pages);
            setTransactionsTotalCount(result.total_count);
        } catch (error: any) {
            toast.showError(error?.message ?? 'Failed to load account transactions');
            setTransactions([]);
        } finally {
            setTransactionsLoading(false);
        }
    }, [toast]);

    const fetchSmsNotes = useCallback(async () => {
        if (!customer?.id) return;
        setSmsNotesLoading(true);
        try {
            const data = await getCustomerSmsNotes(customer.id);
            setSmsNotes(data);
        } catch (error: any) {
            toast.showError(error?.message ?? 'Failed to load SMS and notes');
            setSmsNotes([]);
        } finally {
            setSmsNotesLoading(false);
        }
    }, [customer?.id, toast]);

    useEffect(() => {
        if (!customer?.id) return;
        let isMounted = true;
        setCustomerDocsLoading(true);
        getCustomerDocuments(customer.id)
            .then((data) => {
                if (!isMounted) return;
                setCustomerDocs(data || []);
            })
            .catch((error: any) => {
                if (!isMounted) return;
                toast.showError(error?.message ?? 'Failed to load documents');
                setCustomerDocs([]);
            })
            .finally(() => {
                if (isMounted) {
                    setCustomerDocsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [customer?.id, toast]);

    const handleAccountClick = (account: PlAccountResponse) => {
        setSelectedAccount(account);
        setTransactionsOpen(true);
        loadTransactions(account.id!, 1);
    };

    const handleShowProducts = async (account: PlAccountResponse) => {
        setProductsAccount(account);
        setProductsDrawerOpen(true);
        setProductsLoading(true);
        setProducts([]);
        try {
            const data = await getDebtorProductsByAccountId(account.id!);
            if (isMountedRef.current) {
                setProducts(data || []);
            }
        } catch (error: any) {
            if (isMountedRef.current) {
                toast.showError(error?.message ?? 'Failed to load debtor products');
            }
        } finally {
            if (isMountedRef.current) {
                setProductsLoading(false);
            }
        }
    };

    const openMessageDialog = (mode: 'sms' | 'note') => {
        setMessageDialogMode(mode);
        setMessageText('');
        setMessageError(false);
        setMessageDialogOpen(true);
    };

    const closeMessageDialog = () => {
        setMessageDialogOpen(false);
        setMessageText('');
        setMessageError(false);
    };

    const handleSubmitMessage = async () => {
        const trimmed = messageText.trim();
        if (!trimmed) {
            setMessageError(true);
            return;
        }
        if (!customer?.id) {
            toast.showError('Error', 'Customer not loaded');
            return;
        }

        setMessageSubmitting(true);
        try {
            if (messageDialogMode === 'sms') {
                await sendCustomerSms(customer.id, trimmed);
                toast.showSuccess('Success', 'SMS sent');
            } else {
                await addCustomerNote(customer.id, trimmed);
                toast.showSuccess('Success', 'Note added');
            }
            closeMessageDialog();
        } catch (error: any) {
            toast.showError('Error', error?.message ?? 'Request failed');
        } finally {
            setMessageSubmitting(false);
        }
    };

    const handleDownloadQr = async () => {
        if (!customer?.id) {
            toast.showError('Customer not loaded');
            return;
        }
        try {
            const cardNum = qrCardNumber.trim() || `${customer.customer_number || customer.id}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;
            const response = await apiClient.get('/customer/generate-qr', {
                params: { customer_id: customer.id, card_number: cardNum },
                responseType: 'blob',
            });
            const blob = response.data as Blob;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `member_qr_${customer.id}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            setQrDialogOpen(false);
            toast.showSuccess('QR code downloaded');
        } catch (error: any) {
            toast.showError(error?.message ?? 'Failed to generate QR code');
        }
    };

    const filteredSmsNotes = useMemo(() => {
        if (smsNotesFilter === 'all') return smsNotes;
        return smsNotes.filter((note) => note.type === smsNotesFilter);
    }, [smsNotes, smsNotesFilter]);

    const renderAccountsTable = (
        list: PlAccountResponse[],
        emptyMessage: string,
        variant: 'loan' | 'savings' | 'fd',
        loading?: boolean
    ) => {
        const isLoading = typeof loading === 'boolean' ? loading : accountsLoading;
        if (isLoading) {
            return <Text tone="secondary">Loading accounts...</Text>;
        }

        if (list.length === 0) {
            return <Text tone="secondary">{emptyMessage}</Text>;
        }

        return (
            <Box
                style={{
                    width: '100%',
                    overflowX: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                    background: '#ffffff'
                }}
            >
                <Table label="Accounts">
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>
                                <Text size="xsmall">Account Number</Text>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text size="xsmall">Account Type Name</Text>
                            </TableHeaderCell>
                            <TableHeaderCell align="right">
                                <Text size="xsmall">Balance</Text>
                            </TableHeaderCell>
                            {variant === 'loan' && (
                                <>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Last Transaction</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Interest Rate</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Period (Months)</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Issue Date</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Issued Amount</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Installment</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Interest</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Past Due</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Due Interest</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">No of Installments</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">FD Savings No</Text>
                                    </TableHeaderCell>
                                </>
                            )}
                            {variant === 'savings' && (
                                <>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Last Transaction</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Period (Days/Months)</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Open Date</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Due Date</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Due Interest</Text>
                                    </TableHeaderCell>
                                </>
                            )}
                            {variant === 'fd' && (
                                <>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Interest Rate</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Last Transaction</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Period (Days/Months)</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Open Date</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Due Date</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Due Interest</Text>
                                    </TableHeaderCell>
                                </>
                            )}
                            <TableHeaderCell align="left">
                                <Text size="xsmall">Actions</Text>
                            </TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {list.map((account) => (
                            <TableRow key={account.id ?? account.ref_account_number}>
                                <TableCell>
                                    <Box
                                        cursor="pointer"
                                        onClick={() => handleAccountClick(account)}
                                    >
                                        <Text size="small" tone="info">
                                            {account.ref_account_number ?? '-'}
                                        </Text>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Text size="small">
                                        {account.account_type?.name_ln1 ?? '-'}
                                    </Text>
                                </TableCell>
                                <TableCell align="right">
                                    <Text size="small">
                                        {formatCurrency(account.balance ?? 0)}
                                    </Text>
                                </TableCell>
                                {variant === 'loan' && (
                                    <>
                                        <TableCell>
                                            <Text size="small">{formatDate(account.last_transaction_date ?? null)}</Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">
                                                {account.interest_rate == null ? '-' : `${account.interest_rate}%`}
                                            </Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">
                                                {account.period == null ? '-' : account.period}
                                            </Text>
                                        </TableCell>
                                        <TableCell>
                                            <Text size="small">
                                                {formatDate(account.open_date ?? null)}
                                            </Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">
                                                {account.approved == null ? '-' : formatCurrency(account.approved ?? 0)}
                                            </Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">
                                                {account.capital_installment == null ? '-' : formatCurrency(account.capital_installment ?? 0)}
                                            </Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">
                                                {account.interest == null ? '-' : formatCurrency(account.interest ?? 0)}
                                            </Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">
                                                {account.past_due_amount == null ? '-' : formatCurrency(account.past_due_amount ?? 0)}
                                            </Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">
                                                {account.due_interest == null ? '-' : formatCurrency(account.due_interest ?? 0)}
                                            </Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">
                                                {(account.past_due_amount == null) || (account.capital_installment == null) || (account.capital_installment == 0) ? '-' : formatInteger((account.past_due_amount / account.capital_installment) || 0)}
                                            </Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">
                                                {account.fd_account_id_for_loan == null
                                                    ? '-'
                                                    : accountMap.get(account.fd_account_id_for_loan)?.ref_account_number ?? '-'}
                                            </Text>
                                        </TableCell>
                                    </>
                                )}
                                {variant === 'savings' && (
                                    <>
                                        <TableCell>
                                            <Text size="small">{formatDate(account.last_transaction_date ?? null)}</Text>
                                        </TableCell>
                                        <TableCell>
                                            <Text size="small">{account.period ?? null}</Text>
                                        </TableCell>
                                        <TableCell>
                                            <Text size="small">{formatDate(account.open_date ?? null)}</Text>
                                        </TableCell>
                                        <TableCell>
                                            <Text size="small">{formatDate(account.reneve_date ?? null)}</Text>
                                        </TableCell>
                                        <TableCell>
                                            <Text size="small">{formatCurrency(account.due_interest ?? 0)}</Text>
                                        </TableCell>
                                    </>
                                )}
                                {variant === 'fd' && (
                                    <>
                                        <TableCell align="right">
                                            <Text size="small">
                                                {account.interest_rate == null ? '-' : `${account.interest_rate}%`}
                                            </Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">{formatDate(account.last_transaction_date ?? null)}</Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">{account.period ?? null}</Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">{formatDate(account.open_date ?? null)}</Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">{formatDate(account.reneve_date ?? null)}</Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">{formatCurrency(account.due_interest ?? 0)}</Text>
                                        </TableCell>
                                    </>
                                )}
                                <TableCell align="left">
                                    <Actions>
                                        <Button
                                            variant="solid"
                                            size="small"
                                            tone="brandAccent"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAccountClick(account);
                                            }}
                                        >
                                            See Last Transactions
                                        </Button>
                                        {variant === 'loan' && (
                                            <Button
                                                size="small"
                                                variant="soft"
                                                tone="formAccent"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.open(`/loan-schedule-print?accountId=${account.id}`, '_blank');
                                                }}
                                            >
                                                Repayment Schedule
                                            </Button>
                                        )}
                                        {variant === 'savings' && (
                                            <Button
                                                size="small"
                                                tone="formAccent"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedAccountForStandingOrder(account);
                                                    setStandingOrderDrawerOpen(true);
                                                }}
                                            >
                                                Standing Orders
                                            </Button>
                                        )}
                                        {variant === 'fd' && (
                                            <Button
                                                size="small"
                                                tone="formAccent"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.open(`/fd-renewal-letter?accountId=${account.id}`, '_blank');
                                                }}
                                            >
                                                Renewal Letter
                                            </Button>
                                        )}
                                    </Actions>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box >
        );
    };

    if (loading) return <Loading message="Loading customer profile..." />;

    if (taxSubmitting) return <Loading message="Submitting tax information..." />;

    return (
        <Box paddingY="medium">
            <Stack space="large">
                {/* Header Toolbar */}
                <Box
                    padding="medium"
                    borderRadius="large"
                    style={{
                        background: '#ffffffff',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '16px',
                        flexWrap: 'wrap'
                    }}
                >
                    <Stack space="xsmall">
                        <Text size="large" weight="strong" tone="neutral">
                            Customer Credit Profile
                        </Text>
                        <Text tone="secondary" size="small">
                            Manage accounts, loans, standing orders, and guarantees in one unified view.
                        </Text>
                    </Stack>
                    <Inline space="small">
                        <Button
                            variant="solid"
                            tone="brandAccent"
                            size='small'
                            onClick={() => setStatementDrawerOpen(true)}
                            icon={<IconDocument />}
                        >
                            Transaction Statement
                        </Button>
                        <Button
                            variant="solid"
                            tone="formAccent"
                            size='small'
                            onClick={() => setCustomerSearchDrawerOpen(true)}
                            icon={<IconSearch />}
                        >
                            Search Customer
                        </Button>
                    </Inline>
                </Box>

                {/* Profile Dashboard Card */}
                <Box
                    borderRadius="large"
                    style={{
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                    }}
                >
                    {/* Top Decorative Gradient Accent Bar */}
                    <div style={{ height: '6px', background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)' }} />

                    <Box padding="medium">
                        <Columns space="large" collapseBelow="tablet">
                            {/* Column 1: Photos & Signature */}
                            <Column width="1/4">
                                <Box
                                    padding="medium"
                                    style={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '24px',
                                    }}
                                >
                                    <Box style={{ textAlign: 'center', width: '100%' }}>
                                        <Box
                                            style={{
                                                width: 140,
                                                height: 140,
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                backgroundColor: '#f1f5f9',
                                                border: '4px solid #ffffff',
                                                boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.3), 0 8px 10px -6px rgba(59, 130, 246, 0.3)',
                                                margin: '0 auto 12px auto',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {photoSrc ? (
                                                <img
                                                    src={photoSrc}
                                                    onError={() => setPhotoError(true)}
                                                    alt={customer?.full_name_ln1 ?? 'Customer photo'}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                                                    onClick={() => setShowPhotoModal(true)}
                                                />
                                            ) : (
                                                <Text tone="secondary" size="small">No Photo</Text>
                                            )}
                                        </Box>
                                        <Text size="small" weight="strong" tone="secondary">Profile Photo</Text>
                                    </Box>
 
                                    <Box style={{ width: '100%', textAlign: 'center' }}>
                                        <Box
                                            style={{
                                                width: '100%',
                                                height: 90,
                                                borderRadius: '12px',
                                                overflow: 'hidden',
                                                backgroundColor: '#ffffff',
                                                border: '1.5px dashed #cbd5e1',
                                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginBottom: '8px'
                                            }}
                                        >
                                            {signatureSrc ? (
                                                <img
                                                    src={signatureSrc}
                                                    onError={() => setSigError(true)}
                                                    alt="Customer signature"
                                                    style={{ width: '90%', height: '90%', objectFit: 'contain', cursor: 'pointer' }}
                                                    onClick={() => setShowSignatureModal(true)}
                                                />
                                            ) : (
                                                <Text tone="secondary" size="small">No Signature</Text>
                                            )}
                                        </Box>
                                        <Text size="small" weight="strong" tone="secondary">Signature</Text>
                                    </Box>
                                </Box>
                            </Column>

                            {/* Column 2: Personal Details Grid */}
                            <Column width="1/2">
                                <Stack space="medium">
                                    <Inline space="small" alignY="center">
                                        <Text size="large" weight="strong" tone="neutral">
                                            {customer?.full_name_ln1 ?? '-'}
                                        </Text>
                                        {customer?.status === 1 ? (
                                            <Badge tone="positive">Active</Badge>
                                        ) : (
                                            <Badge tone="caution">Inactive</Badge>
                                        )}
                                    </Inline>

                                    <Box
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                            gap: '16px'
                                        }}
                                    >
                                        <Box
                                            padding="small"
                                            borderRadius="standard"
                                            style={{
                                                border: '1px solid #e2e8f0',
                                                background: '#ffffff',
                                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
                                            }}
                                        >
                                            <Stack space="xxsmall">
                                                <Text tone="secondary" size="xsmall">Customer Number</Text>
                                                <Text weight="strong" size="standard">{customer?.customer_number ?? '-'}</Text>
                                            </Stack>
                                        </Box>

                                        <Box
                                            padding="small"
                                            borderRadius="standard"
                                            style={{
                                                border: '1px solid #e2e8f0',
                                                background: '#ffffff',
                                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
                                            }}
                                        >
                                            <Stack space="xxsmall">
                                                <Inline space="xsmall" alignY="center">
                                                    <Text tone="secondary" size="xsmall">NIC</Text>
                                                    <Badge tone={customer?.is_nic_verified === 1 ? 'positive' : 'caution'}>
                                                        {customer?.is_nic_verified === 1 ? 'Verified' : 'Not Verified'}
                                                    </Badge>
                                                </Inline>
                                                <Text weight="strong" size="standard">{customer?.nic ?? '-'}</Text>
                                            </Stack>
                                        </Box>

                                        <Box
                                            padding="small"
                                            borderRadius="standard"
                                            style={{
                                                border: '1px solid #e2e8f0',
                                                background: '#ffffff',
                                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
                                            }}
                                        >
                                            <Stack space="xxsmall">
                                                <Text tone="secondary" size="xsmall" icon={<IconMobile />}>Mobile</Text>
                                                <Text weight="strong" size="standard">{customer?.mobile_1 ?? '-'}</Text>
                                            </Stack>
                                        </Box>

                                        <Box
                                            padding="small"
                                            borderRadius="standard"
                                            style={{
                                                border: '1px solid #e2e8f0',
                                                background: '#ffffff',
                                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
                                            }}
                                        >
                                            <Stack space="xxsmall">
                                                <Text tone="secondary" size="xsmall" icon={<IconMail />}>Email</Text>
                                                <Box style={{ wordBreak: 'break-all' }}>
                                                    <Text weight="strong" size="standard">{customer?.e_mail ?? '-'}</Text>
                                                </Box>
                                            </Stack>
                                        </Box>

                                        <Box
                                            padding="small"
                                            borderRadius="standard"
                                            style={{
                                                border: '1px solid #e2e8f0',
                                                background: '#ffffff',
                                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
                                            }}
                                        >
                                            <Stack space="xxsmall">
                                                <Text tone="secondary" size="xsmall">Customer Since</Text>
                                                <Text weight="strong" size="standard">{formatDate(customer?.member_date ?? null)}</Text>
                                            </Stack>
                                        </Box>
                                        
                                        <Box
                                            padding="small"
                                            borderRadius="standard"
                                            style={{
                                                border: '1px solid #e2e8f0',
                                                background: '#ffffff',
                                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
                                            }}
                                        >
                                            <Stack space="xxsmall">
                                                <Text tone="secondary" size="xsmall">Customer DoB</Text>
                                                <Text weight="strong" size="standard">{formatDate(customer?.date_of_birth ?? null)}</Text>
                                            </Stack>
                                        </Box>

                                        <Box
                                            padding="small"
                                            borderRadius="standard"
                                            style={{
                                                border: '1px solid #e2e8f0',
                                                background: '#ffffff',
                                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
                                            }}
                                        >
                                            <Stack space="xxsmall">
                                                <Text tone="secondary" size="xsmall">Customer Type</Text>
                                                <Text weight="strong" size="standard">{customer?.customer_type?.type_ln1 ?? '-'}</Text>
                                            </Stack>
                                        </Box>
                                    </Box>

                                    <Box
                                        padding="small"
                                        borderRadius="standard"
                                        style={{
                                            border: '1px solid #e2e8f0',
                                            background: '#ffffff',
                                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
                                        }}
                                    >
                                        <Stack space="xxsmall">
                                            <Text tone="secondary" size="xsmall" icon={<IconLocation />}>Address</Text>
                                            <Text weight="strong">{customer?.address_ln1 ?? '-'}</Text>
                                        </Stack>
                                    </Box>

                                    <Inline space="small" alignY="center">
                                        <Button
                                            onClick={() => openMessageDialog('sms')}
                                            variant="soft"
                                            tone="brandAccent"
                                            size="small"
                                        >
                                            Send SMS
                                        </Button>
                                        <Button
                                            onClick={() => openMessageDialog('note')}
                                            variant="soft"
                                            tone="formAccent"
                                            size="small"
                                        >
                                            Add Note
                                        </Button>
                                        <Box display="flex" alignItems="center" gap="small">
                                            <Button
                                                onClick={async () => {
                                                    if (!customer?.id) {
                                                        toast.showError('Customer not loaded');
                                                        return;
                                                    }
                                                    try {
                                                        const resp = await getCustomerTaxInfo(customer.id);
                                                        setTaxHasStatus(!!resp.has_status);
                                                        setTaxEditable(!!resp.editable);
                                                        if (resp.tax_info) {
                                                            setTaxTin(resp.tax_info.tin_number || '');
                                                            setTaxValidUntil(resp.tax_info.valid_until ? String(resp.tax_info.valid_until).split('T')[0] : '');
                                                            setTaxExistingFile(resp.tax_info.file || null);
                                                        } else {
                                                            setTaxTin('');
                                                            setTaxValidUntil('');
                                                            setTaxExistingFile(null);
                                                        }
                                                        setTaxLatter('');
                                                        setTaxFile(null);
                                                        setTaxDialogOpen(true);
                                                    } catch (err: any) {
                                                        toast.showError(err?.message ?? 'Failed to load tax status');
                                                    }
                                                }}
                                                variant="soft"
                                                tone="critical"
                                                size="small"
                                            >
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                    <span>Tax Remove: </span>
                                                    <span style={{ color: taxHasStatus ? '#16a34a' : '#b45309', fontWeight: 600 }}>
                                                        {taxHasStatus === undefined ? 'Loading...' : taxHasStatus ? 'Enabled' : 'Not Enabled'}
                                                    </span>
                                                </span>
                                            </Button>
                                        </Box>
                                        <Button
                                            onClick={() => setQrDialogOpen(true)}
                                            variant="soft"
                                            size="small"
                                            icon={<IconDownload />}
                                        >
                                            Download QR
                                        </Button>
                                    </Inline>
                                </Stack>
                            </Column>

                            {/* Column 3: Trust & Verification KPI Widgets */}
                            <Column width="1/4">
                                <Box
                                    style={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        gap: '16px'
                                    }}
                                >
                                    <Box
                                        padding="medium"
                                        borderRadius="large"
                                        style={{
                                            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                                            border: '1px solid #bbf7d0',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px',
                                            boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.1)'
                                        }}
                                    >
                                        <Text tone="secondary" size="xsmall" weight="strong">KYC Verification</Text>
                                        <Inline space="xsmall" alignY="center">
                                            <Badge tone="positive">Verified</Badge>
                                        </Inline>
                                    </Box>

                                    <Box
                                        padding="medium"
                                        borderRadius="large"
                                        style={{
                                            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                            border: '1px solid #bfdbfe',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px',
                                            boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.1)'
                                        }}
                                    >
                                        <Text tone="secondary" size="xsmall" weight="strong">Risk Assessment</Text>
                                        <Inline space="xsmall" alignY="center">
                                            <Badge tone="positive">Low Risk</Badge>
                                        </Inline>
                                    </Box>

                                    <Box
                                        padding="medium"
                                        borderRadius="large"
                                        style={{
                                            background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                                            border: '1px solid #e9d5ff',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            textAlign: 'center',
                                            boxShadow: '0 10px 15px -3px rgba(139, 92, 246, 0.1)'
                                        }}
                                    >
                                        <Text tone="secondary" size="xsmall" weight="strong">Credit Score</Text>
                                        <Box style={{ color: '#7c3aed', fontSize: '36px', fontWeight: 'bold', lineHeight: '1' }}>
                                            720
                                        </Box>
                                        <Text size="xsmall" tone="secondary">Excellent Range</Text>
                                    </Box>
                                </Box>
                            </Column>
                        </Columns>
                    </Box>
                </Box>

                <Box
                    background="surface"
                    padding="medium"
                    borderRadius="large"
                    style={{ border: '1px solid #e5e5e5' }}
                >
                    <TabsProvider>
                        <Stack space="medium">
                            <Tabs label="Customer details" divider='full'>
                                <Tab>Overview</Tab>
                                {canViewAccountInfo && (
                                    <>
                                        <Tab>Loans</Tab>
                                        <Tab>Savings</Tab>
                                        <Tab>Fixed Deposits</Tab>
                                        <Tab>Debtors</Tab>
                                        <Tab>Guarantors</Tab>
                                        <Tab>Documents</Tab>
                                        <Tab>SMS & Notes</Tab>
                                        <Tab>Other Info</Tab>
                                    </>
                                )}
                            </Tabs>
                            {/* Per-panel checkbox only — global checkbox removed */}
                            <TabPanels>
                                <TabPanel>
                                    <Stack space="medium">
                                        {accountsLoading && (
                                            <Text tone="secondary">Loading account summary...</Text>
                                        )}
                                        <Columns space="medium" collapseBelow="tablet">
                                            <Column width="1/5">
                                                <Box
                                                    padding="medium"
                                                    borderRadius="large"
                                                    style={{
                                                        border: '1px solid #e2e8f0',
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                                        background: '#ffffff',
                                                        borderTop: '4px solid #3b82f6',
                                                        minHeight: '130px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <Stack space="xsmall">
                                                        <Text tone="secondary" size="small" weight="strong">Total Savings & FD</Text>
                                                        <Text weight="strong" size="large">{formatCurrency(totals.totalSavings)}</Text>
                                                        <Badge tone={totals.savingsPercent > 100 ? 'positive' : 'caution'}>
                                                            {totals.savingsPercent.toFixed(2)}%
                                                        </Badge>
                                                    </Stack>
                                                </Box>
                                            </Column>
                                            <Column width="1/5">
                                                <Box
                                                    padding="medium"
                                                    borderRadius="large"
                                                    style={{
                                                        border: '1px solid #e2e8f0',
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                                        background: '#ffffff',
                                                        borderTop: '4px solid #f59e0b',
                                                        minHeight: '130px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <Stack space="xsmall">
                                                        <Text tone="secondary" size="small" weight="strong">Total Loans</Text>
                                                        <Text weight="strong" size="large">{formatCurrency(totals.totalLoans)}</Text>
                                                        <Badge tone={totals.loanPercent > 100 ? 'caution' : 'positive'}>
                                                            {totals.loanPercent.toFixed(2)}%
                                                        </Badge>
                                                    </Stack>
                                                </Box>
                                            </Column>
                                            <Column width="1/5">
                                                <Box
                                                    padding="medium"
                                                    borderRadius="large"
                                                    style={{
                                                        border: '1px solid #e2e8f0',
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                                        background: '#ffffff',
                                                        borderTop: '4px solid #ef4444',
                                                        minHeight: '130px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <Stack space="xsmall">
                                                        <Text tone="secondary" size="small" weight="strong">Total Past Due</Text>
                                                        <Text weight="strong" size="large" tone="critical">{formatCurrency(totals.totalPastDue)}</Text>
                                                        <Badge tone={totals.pastDuePercent > 100 ? 'caution' : 'positive'}>
                                                            {totals.pastDuePercent.toFixed(2)}%
                                                        </Badge>
                                                    </Stack>
                                                </Box>
                                            </Column>
                                            <Column width="1/5">
                                                <Box
                                                    padding="medium"
                                                    borderRadius="large"
                                                    style={{
                                                        border: '1px solid #e2e8f0',
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                                        background: '#ffffff',
                                                        borderTop: '4px solid #10b981',
                                                        minHeight: '130px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <Stack space="xsmall">
                                                        <Text tone="secondary" size="small" weight="strong">Total FD</Text>
                                                        <Text weight="strong" size="large">{formatCurrency(totals.totalFd)}</Text>
                                                    </Stack>
                                                </Box>
                                            </Column>
                                            <Column width="1/5">
                                                <Box
                                                    padding="medium"
                                                    borderRadius="large"
                                                    style={{
                                                        border: '1px solid #e2e8f0',
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                                        background: '#ffffff',
                                                        borderTop: '4px solid #8b5cf6',
                                                        minHeight: '130px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <Stack space="xsmall">
                                                        <Text tone="secondary" size="small" weight="strong">Total Shares</Text>
                                                        <Text weight="strong" size="large">{formatCurrency(totals.totalShares)}</Text>
                                                    </Stack>
                                                </Box>
                                            </Column>
                                        </Columns>
                                    </Stack>
                                </TabPanel>
                                {canViewAccountInfo && (
                                    <>
                                        <TabPanel>
                                            <Stack space="medium">
                                                <Box paddingBottom="small">
                                                    <Inline space="small" alignY="center">
                                                        <Text weight="strong">Loan Accounts</Text>
                                                        <select
                                                            value={loanStatusFilter}
                                                            onChange={(e) => setLoanStatusFilter(e.target.value)}
                                                            style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc' }}
                                                        >
                                                            <option value="active">Active</option>
                                                            <option value="applications">Loan Applications</option>
                                                            <option value="inactive">Inactive</option>
                                                            <option value="all">All</option>
                                                        </select>
                                                        {loanStatusFilter === 'active' && <Badge tone="positive">Active</Badge>}
                                                        {loanStatusFilter === 'applications' && <Badge tone="info">Applications</Badge>}
                                                        {loanStatusFilter === 'inactive' && <Badge tone="critical">Inactive</Badge>}
                                                    </Inline>
                                                </Box>
                                                <Box paddingBottom="small">
                                                    <Box style={{ maxWidth: 300 }}>
                                                        <TextField
                                                            label=""
                                                            placeholder="Search by Account Number..."
                                                            value={loanSearch}
                                                            onChange={(e) => setLoanSearch(e.currentTarget.value)}
                                                        />
                                                    </Box>
                                                </Box>
                                                {(() => {
                                                    const filtered = loanAccounts.filter(acc => {
                                                        const matchesSearch = acc.ref_account_number?.toLowerCase().includes(loanSearch.toLowerCase());
                                                        if (loanStatusFilter === 'all') return matchesSearch;
                                                        if (loanStatusFilter === 'active') return matchesSearch && acc.status === 1 && (acc.balance ?? 0) > 0;
                                                        if (loanStatusFilter === 'inactive') return matchesSearch && acc.status === 1 && (acc.balance ?? 0) === 0;
                                                        if (loanStatusFilter === 'applications') return matchesSearch && acc.status === 0 && (acc.balance ?? 0) === 0;
                                                        return matchesSearch;
                                                    });
                                                    return renderAccountsTable(filtered, 'No loan accounts found.', 'loan');
                                                })()}
                                            </Stack>
                                        </TabPanel>
                                        <TabPanel>
                                            <Stack space="medium">
                                                <Box paddingBottom="small">
                                                    <Inline space="small" alignY="center">
                                                        <Text weight="strong">Saving Accounts</Text>
                                                        <select
                                                            value={savingsStatusFilter}
                                                            onChange={(e) => setSavingsStatusFilter(e.target.value)}
                                                            style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc' }}
                                                        >
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                            <option value="dormant">Dormant</option>
                                                            <option value="all">All</option>
                                                        </select>
                                                        {savingsStatusFilter === 'active' && <Badge tone="positive">Active</Badge>}
                                                        {savingsStatusFilter === 'inactive' && <Badge tone="critical">Inactive</Badge>}
                                                        {savingsStatusFilter === 'dormant' && <Badge tone="neutral">Dormant</Badge>}
                                                    </Inline>
                                                </Box>
                                                <Box paddingBottom="small">
                                                    <Box style={{ maxWidth: 300 }}>
                                                        <TextField
                                                            label=""
                                                            placeholder="Search by Account Number..."
                                                            value={savingsSearch}
                                                            onChange={(e) => setSavingsSearch(e.currentTarget.value)}
                                                        />
                                                    </Box>
                                                </Box>
                                                {(() => {
                                                    const nonFdSavings = savingsExcludingFd;
                                                    const allSavingsAccounts = [...nonFdSavings, ...dormantAccounts.filter(a => a.account_type?.pl_account_category_id === 1 && ![15, 16, 17].includes(a.account_type?.interest_policy ?? -1))];
                                                    const filtered = allSavingsAccounts.filter(acc => {
                                                        const matchesSearch = acc.ref_account_number?.toLowerCase().includes(savingsSearch.toLowerCase());
                                                        if (savingsStatusFilter === 'all') return matchesSearch;
                                                        if (savingsStatusFilter === 'active') return matchesSearch && acc.status === 1;
                                                        if (savingsStatusFilter === 'inactive') return matchesSearch && acc.status === 0;
                                                        if (savingsStatusFilter === 'dormant') return matchesSearch && acc.status === 9;
                                                        return matchesSearch;
                                                    });
                                                    return renderAccountsTable(filtered, 'No savings accounts found.', 'savings');
                                                })()}
                                            </Stack>
                                        </TabPanel>
                                        <TabPanel>
                                            <Stack space="medium">
                                                <Box paddingBottom="small">
                                                    <Inline space="small" alignY="center">
                                                        <Text weight="strong">Fixed Deposit Accounts</Text>
                                                        <select
                                                            value={fdStatusFilter}
                                                            onChange={(e) => setFdStatusFilter(e.target.value)}
                                                            style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc' }}
                                                        >
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                            <option value="all">All</option>
                                                        </select>
                                                        {fdStatusFilter === 'active' && <Badge tone="positive">Active</Badge>}
                                                        {fdStatusFilter === 'inactive' && <Badge tone="critical">Inactive</Badge>}
                                                    </Inline>
                                                </Box>
                                                <Box paddingBottom="small">
                                                    <Box style={{ maxWidth: 300 }}>
                                                        <TextField
                                                            label=""
                                                            placeholder="Search by Account Number..."
                                                            value={fdSearch}
                                                            onChange={(e) => setFdSearch(e.currentTarget.value)}
                                                        />
                                                    </Box>
                                                </Box>
                                                {(() => {
                                                    const filtered = fdAccounts.filter(acc => {
                                                        const matchesSearch = acc.ref_account_number?.toLowerCase().includes(fdSearch.toLowerCase());
                                                        if (fdStatusFilter === 'all') return matchesSearch;
                                                        if (fdStatusFilter === 'active') return matchesSearch && (acc.balance ?? 0) > 0;
                                                        if (fdStatusFilter === 'inactive') return matchesSearch && (acc.balance ?? 0) === 0;
                                                        return matchesSearch;
                                                    });
                                                    return renderAccountsTable(filtered, 'No fixed deposit accounts found.', 'fd');
                                                })()}
                                            </Stack>
                                        </TabPanel>
                                        <TabPanel>
                                            <Stack space="medium">
                                                <Box paddingBottom="small">
                                                    <Inline space="small" alignY="center">
                                                        <Text weight="strong">Debtor Accounts</Text>
                                                        <select
                                                            value={debtorStatusFilter}
                                                            onChange={(e) => setDebtorStatusFilter(e.target.value)}
                                                            style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc' }}
                                                        >
                                                            <option value="all">All</option>
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                        </select>
                                                        {debtorStatusFilter === 'active' && <Badge tone="positive">Active</Badge>}
                                                        {debtorStatusFilter === 'inactive' && <Badge tone="caution">Inactive</Badge>}
                                                    </Inline>
                                                </Box>
                                                <Box paddingBottom="small">
                                                    <Box style={{ maxWidth: 300 }}>
                                                        <TextField
                                                            label=""
                                                            placeholder="Search by Account Number..."
                                                            value={debtorSearch}
                                                            onChange={(e) => setDebtorSearch(e.currentTarget.value)}
                                                        />
                                                    </Box>
                                                </Box>
                                                {debtorAccountsLoading ? (
                                                    <Text tone="secondary">Loading debtor accounts...</Text>
                                                ) : (() => {
                                                    const filtered = debtorAccounts.filter(acc => {
                                                        const matchesSearch = acc.ref_account_number?.toLowerCase().includes(debtorSearch.toLowerCase());
                                                        const isActive = (acc.balance ?? 0) > 0;
                                                        if (debtorStatusFilter === 'active') return matchesSearch && isActive;
                                                        if (debtorStatusFilter === 'inactive') return matchesSearch && !isActive;
                                                        return matchesSearch;
                                                    });
                                                    if (filtered.length === 0) {
                                                        return <Text tone="secondary">No debtor accounts found.</Text>;
                                                    }
                                                    return (
                                                        <Box
                                                            style={{
                                                                width: '100%',
                                                                overflowX: 'auto',
                                                                border: '1px solid #e2e8f0',
                                                                borderRadius: '12px',
                                                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                                                background: '#ffffff'
                                                            }}
                                                        >
                                                            <Table label="Debtor Accounts">
                                                                <TableHeader>
                                                                    <TableRow>
                                                                        <TableHeaderCell>
                                                                            <Text size="xsmall">Account Number</Text>
                                                                        </TableHeaderCell>
                                                                        <TableHeaderCell>
                                                                            <Text size="xsmall">Account Name</Text>
                                                                        </TableHeaderCell>
                                                                        <TableHeaderCell align="right">
                                                                            <Text size="xsmall">Balance</Text>
                                                                        </TableHeaderCell>
                                                                        <TableHeaderCell>
                                                                            <Text size="xsmall">Status</Text>
                                                                        </TableHeaderCell>
                                                                        <TableHeaderCell align="left">
                                                                            <Text size="xsmall">Actions</Text>
                                                                        </TableHeaderCell>
                                                                    </TableRow>
                                                                </TableHeader>
                                                                <TableBody>
                                                                    {filtered.map((acc) => {
                                                                        const isActive = (acc.balance ?? 0) > 0;
                                                                        return (
                                                                            <TableRow key={acc.id}>
                                                                                <TableCell>
                                                                                    <Box
                                                                                        cursor="pointer"
                                                                                        onClick={() => handleAccountClick(acc)}
                                                                                    >
                                                                                        <Text size="small" tone="info">
                                                                                            {acc.ref_account_number ?? '-'}
                                                                                        </Text>
                                                                                    </Box>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Text size="small">{acc.account_type?.name_ln1 ?? '-'}</Text>
                                                                                </TableCell>
                                                                                <TableCell align="right">
                                                                                    <Text size="small">{formatCurrency(acc.balance ?? 0)}</Text>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Badge tone={isActive ? 'positive' : 'caution'}>
                                                                                        {isActive ? 'Active' : 'Inactive'}
                                                                                    </Badge>
                                                                                </TableCell>
                                                                                <TableCell align="left">
                                                                                    <Actions>
                                                                                        <Button
                                                                                            variant="solid"
                                                                                            size="small"
                                                                                            tone="brandAccent"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleAccountClick(acc);
                                                                                            }}
                                                                                        >
                                                                                            See Last Transactions
                                                                                        </Button>
                                                                                        <Button
                                                                                            size="small"
                                                                                            tone="formAccent"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleShowProducts(acc);
                                                                                            }}
                                                                                        >
                                                                                            Show Products
                                                                                        </Button>
                                                                                    </Actions>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    })}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    );
                                                })()}
                                            </Stack>
                                        </TabPanel>
                                        <TabPanel>
                                            {customerNumber ? (
                                                <GuaranteesTab customerNumber={customerNumber} />
                                            ) : (
                                                <Stack space="medium">
                                                    <Text tone="secondary">Customer number is unavailable.</Text>
                                                </Stack>
                                            )}
                                        </TabPanel>
                                        <TabPanel>
                                            <Stack space="medium">
                                                {customerDocsLoading ? (
                                                    <Text tone="secondary">Loading documents...</Text>
                                                ) : (
                                                    <Table label="Customer documents">
                                                        <TableHeader>
                                                            <TableRow>
                                                                <TableHeaderCell>
                                                                    <Text size="xsmall">Document</Text>
                                                                </TableHeaderCell>
                                                                <TableHeaderCell>
                                                                    <Text size="xsmall">Action</Text>
                                                                </TableHeaderCell>
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {customerDocs.length > 0 ? (
                                                                customerDocs.map((doc, index) => (
                                                                    <TableRow key={doc.id ?? index}>
                                                                        <TableCell>
                                                                            <Text size="small">{doc.document_name ?? 'Document'}</Text>
                                                                        </TableCell>
<TableCell>
                                                                             {doc.file_url ? (
                                                                                 <Button
                                                                                     variant="ghost"
                                                                                     onClick={() => window.open(getFileUrl(doc.file_url) ?? '', '_blank', 'noopener,noreferrer')}
                                                                                 >
                                                                                     View
                                                                                 </Button>
                                                                             ) : (
                                                                                 <Text size="small" tone="secondary">Unavailable</Text>
                                                                             )}
                                                                         </TableCell>
                                                                    </TableRow>
                                                                ))
                                                            ) : (
                                                                <TableRow>
                                                                    <TableCell colspan={2} align="center">
                                                                        <Text size="small" tone="secondary">No documents found.</Text>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                )}
                                            </Stack>
                                        </TabPanel>
                                        <TabPanel>
<SmsNotesTabContent
                                                smsNotesFilter={smsNotesFilter}
                                                setSmsNotesFilter={setSmsNotesFilter}
                                                smsNotesLoading={smsNotesLoading}
                                                filteredSmsNotes={filteredSmsNotes}
                                                onRefresh={fetchSmsNotes}
                                            />
                                        </TabPanel>
                                        <TabPanel>
                                            <OtherInfoTabContent
                                                customer={customer}
                                                nominee={nominee}
                                                customerInfos={customerInfos}
                                                customerDocs={customerDocs}
                                                nicPhotos={nicPhotos}
                                                nomineeLoading={nomineeLoading}
                                                customerInfosLoading={customerInfosLoading}
                                                customerDocsLoading={customerDocsLoading}
                                                nicPhotosLoading={nicPhotosLoading}
                                            />
                                        </TabPanel>
                                    </>
                                )}
                            </TabPanels>
                        </Stack>
                    </TabsProvider>
                </Box>
            </Stack>
            <Drawer
                title={`Transaction History - ${selectedAccount?.ref_account_number ?? ''}`}
                width="large"
                open={transactionsOpen}
                onClose={() => setTransactionsOpen(false)}
            >
                {transactionsLoading ? (
                    <Box paddingBottom="medium">
                        <Text size="small" tone="secondary">Loading transactions...</Text>
                    </Box>
                ) : transactions.length > 0 ? (
                    <Stack space="medium">
                        <Text size="small" tone="info">Branch: {transactions[0]?.pl_account?.branch?.name_ln1 ?? user?.active_branch?.name_ln1}</Text>
                        <Table label="Transaction History">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell><Text size="xsmall" weight="strong">Transaction Number</Text></TableHeaderCell>
                                    <TableHeaderCell><Text size="xsmall" weight="strong">Updated Date</Text></TableHeaderCell>
                                    <TableHeaderCell align="right"><Text size="xsmall" weight="strong">Debit</Text></TableHeaderCell>
                                    <TableHeaderCell align="right"><Text size="xsmall" weight="strong">Credit</Text></TableHeaderCell>
                                    <TableHeaderCell align="right"><Text size="xsmall" weight="strong">Balance</Text></TableHeaderCell>
                                    <TableHeaderCell align="right"><Text size="xsmall" weight="strong">Interest</Text></TableHeaderCell>
                                    <TableHeaderCell align="right"><Text size="xsmall" weight="strong">Penalty</Text></TableHeaderCell>
                                    <TableHeaderCell align="right"><Text size="xsmall" weight="strong">Fund</Text></TableHeaderCell>
                                    <TableHeaderCell align="right"><Text size="xsmall" weight="strong">Fee</Text></TableHeaderCell>
                                    <TableHeaderCell align="right"><Text size="xsmall" weight="strong">Tax</Text></TableHeaderCell>
                                    <TableHeaderCell><Text size="xsmall" weight="strong">Description</Text></TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {transactions.map((tx) => (
                                    <TableRow key={tx.id}>
                                        <TableCell>
                                            <Box
                                                cursor="pointer"
                                                onClick={() => {
                                                    if (tx.transaction_header?.id) {
                                                        setPrintTxId(tx.transaction_header.id);
                                                    }
                                                }}
                                            >
                                                <Text size="xsmall" tone="info">{tx.transaction_number || '-'}</Text>
                                            </Box>
                                        </TableCell>
                                        <TableCell><Text size="xsmall">{tx.updated_date ? new Date(tx.updated_date).toLocaleString('en-PK') : '-'}</Text></TableCell>
                                        <TableCell align="right"><Text size="xsmall">{tx.debit ? formatCurrency(tx.debit) : '-'}</Text></TableCell>
                                        <TableCell align="right"><Text size="xsmall">{tx.credit ? formatCurrency(tx.credit) : '-'}</Text></TableCell>
                                        <TableCell align="right"><Text size="xsmall">{tx.balance != null ? formatCurrency(tx.balance) : '-'}</Text></TableCell>
                                        <TableCell align="right"><Text size="xsmall">{tx.interest != null ? formatCurrency(tx.interest) : '-'}</Text></TableCell>
                                        <TableCell align="right"><Text size="xsmall">{tx.penalty != null ? formatCurrency(tx.penalty) : '-'}</Text></TableCell>
                                        <TableCell align="right"><Text size="xsmall">{tx.fund != null ? formatCurrency(tx.fund) : '-'}</Text></TableCell>
                                        <TableCell align="right"><Text size="xsmall">{tx.fee != null ? formatCurrency(tx.fee) : '-'}</Text></TableCell>
                                        <TableCell align="right"><Text size="xsmall">{tx.tax_amount != null ? formatCurrency(tx.tax_amount) : '-'}</Text></TableCell>
                                        <TableCell><Text size="xsmall">{tx.description || '-'}</Text></TableCell>
                                        
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Columns space="small">
                            <Column width="1/3">
                                <Box display="flex" justifyContent="flexStart" marginTop="small">
                                    <Text size="small" tone="secondary">
                                        {`Total: ${transactionsTotalCount} records | Page ${transactionsPage} of ${transactionsTotalPages}`}
                                    </Text>
                                </Box>
                            </Column>
                            <Column width="2/3">
                                <Box display="flex" justifyContent="flexEnd" marginTop="small">
                                    <Pagination
                                        label="Transaction history pages"
                                        page={transactionsPage}
                                        total={transactionsTotalPages > 0 ? transactionsTotalPages : 1}
                                        linkProps={({ page }) => ({
                                            href: '#',
                                            onClick: (e: React.MouseEvent) => {
                                                e.preventDefault();
                                                if (selectedAccount) loadTransactions(selectedAccount.id!, page);
                                            }
                                        })}
                                    />
                                </Box>
                            </Column>
                        </Columns>
                    </Stack>
                ) : (
                    <Text tone="secondary">No transactions found.</Text>
                )}
            </Drawer>
            <Drawer
                title={`Products - ${productsAccount?.ref_account_number ?? ''}`}
                width="large"
                open={productsDrawerOpen}
                onClose={() => setProductsDrawerOpen(false)}
            >
                {productsLoading ? (
                    <Box paddingBottom="medium">
                        <Text size="small" tone="secondary">Loading products...</Text>
                    </Box>
                ) : products.length > 0 ? (
                    <Table label="Debtor Products">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell><Text size="xsmall" weight="strong">Product Name</Text></TableHeaderCell>
                                <TableHeaderCell><Text size="xsmall" weight="strong">Description</Text></TableHeaderCell>
                                <TableHeaderCell align="right"><Text size="xsmall" weight="strong">Unit Price</Text></TableHeaderCell>
                                <TableHeaderCell align="right"><Text size="xsmall" weight="strong">Qty</Text></TableHeaderCell>
                                <TableHeaderCell align="right"><Text size="xsmall" weight="strong">Amount</Text></TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {products.map((product) => (
                                <TableRow key={product.id}>
                                    <TableCell><Text size="small">{product.name || '-'}</Text></TableCell>
                                    <TableCell><Text size="small">{product.description || '-'}</Text></TableCell>
                                    <TableCell align="right"><Text size="small">{product.unit_price != null ? formatCurrency(product.unit_price) : '-'}</Text></TableCell>
                                    <TableCell align="right"><Text size="small">{product.qty ?? '-'}</Text></TableCell>
                                    <TableCell align="right"><Text size="small">{product.amount != null ? formatCurrency(product.amount) : '-'}</Text></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <Text tone="secondary">No products found.</Text>
                )}
            </Drawer>
            {selectedAccountForStandingOrder && customer && (
                <StandingOrderDrawer
                    open={standingOrderDrawerOpen}
                    onClose={() => setStandingOrderDrawerOpen(false)}
                    fromAccount={{
                        id: selectedAccountForStandingOrder.id || 0,
                        ref_account_number: selectedAccountForStandingOrder.ref_account_number || '',
                        customer: {
                            id: customer.id || 0,
                            ref_number: customer.customer_number || '',
                            name_ln1: customer.full_name_ln1 || ''
                        }
                    }}
                />
            )}
            {showPhotoModal && photoSrc && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                    onClick={() => setShowPhotoModal(false)}
                >
                    <img
                        src={photoSrc}
                        alt="Customer Large"
                        style={{ maxHeight: '90vh', maxWidth: '90vw', borderRadius: '10px', background: '#fff' }}
                        onClick={(event) => event.stopPropagation()}
                    />
                </div>
            )}
            {showSignatureModal && signatureSrc && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                    onClick={() => setShowSignatureModal(false)}
                >
                    <img
                        src={signatureSrc}
                        alt="Customer Signature Large"
                        style={{ maxHeight: '90vh', maxWidth: '90vw', borderRadius: '10px', background: '#fff' }}
                        onClick={(event) => event.stopPropagation()}
                    />
                </div>
            )}
            <Dialog
                id="customer-message-dialog"
                title={messageDialogMode === 'sms' ? 'Send SMS' : 'Add Note'}
                open={messageDialogOpen}
                onClose={closeMessageDialog}
                width="small"
            >
                <Stack space="medium">
                    <Textarea
                        label="Message"
                        value={messageText}
                        onChange={(event) => {
                            setMessageText(event.currentTarget.value);
                            if (messageError) {
                                setMessageError(false);
                            }
                        }}
                        tone={messageError ? 'critical' : 'neutral'}
                        message={messageError ? 'Message is required' : undefined}
                    />
                    <Actions>
                        <Button variant="ghost" onClick={closeMessageDialog}>
                            Cancel
                        </Button>
                        <Button onClick={handleSubmitMessage} loading={messageSubmitting}>
                            {messageDialogMode === 'sms' ? 'Send' : 'Add'}
                        </Button>
                    </Actions>
                </Stack>
            </Dialog>

            <Dialog
                id="customer-tax-dialog"
                title="Tax Remove"
                open={taxDialogOpen}
                onClose={() => setTaxDialogOpen(false)}
                width="small"
            >
                <Stack space="medium">
                    <Box style={{ marginBottom: '2px' }}>
                        <Text weight='strong'>Tax Remove Letter</Text>
                    </Box>
                    {taxExistingFile && !taxFile ? (
                        <Box style={{ marginBottom: '8px' }}>
                            <Button
                                variant="solid"
                                size="small"
                                onClick={() => window.open(`${config.apiUrl}/customer/files/${taxExistingFile}`, '_blank', 'noopener,noreferrer')}
                            >
                                View uploaded document
                            </Button>
                        </Box>
                    ) : null}
                    {taxEditable ? (
                        <>
                            <input
                                type="file"
                                id="tax-file-upload"
                                accept="application/pdf,image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        setTaxFile(e.target.files[0]);
                                    } else {
                                        setTaxFile(null);
                                    }
                                }}
                            />
                            <Button
                                variant="ghost"
                                size="small"
                                tone={taxFile ? "brandAccent" : "neutral"}
                                icon={taxFile ? <IconTick /> : <IconUpload />}
                                onClick={() => document.getElementById('tax-file-upload')?.click()}
                                loading={taxSubmitting}
                            >
                                {taxFile ? "Uploaded" : "Upload"}
                            </Button>
                        </>
                    ) : null}
                    <TextField
                        label="TIN Number"
                        value={taxTin}
                        onChange={(e) => setTaxTin(e.currentTarget.value)}
                        disabled={!taxEditable}
                    />
                    <DateField
                        label="Valid Until"
                        onlyForToday={false}
                        value={taxValidUntil}
                        onChange={(v) => setTaxValidUntil(v || '')}
                        disabled={!taxEditable}
                    />
                    <Actions>
                        <Button variant="ghost" tone='critical' onClick={() => setTaxDialogOpen(false)}>
                            Cancel
                        </Button>
                        {(!taxHasStatus || taxEditable) && (
                            <Button
                                variant="ghost" tone='brandAccent'
                                onClick={async () => {
                                    if (!customer?.id) {
                                        toast.showError('Customer not loaded');
                                        return;
                                    }
                                    if (!taxFile) {
                                        toast.showError('Please upload tax report');
                                        return;
                                    }
                                    setTaxSubmitting(true);
                                    try {
                                        await uploadCustomerTax(customer.id, {
                                            file: taxFile,
                                            tin_number: taxTin || undefined,
                                            valid_until: taxValidUntil || undefined,
                                            tax_latter: taxLatter || undefined
                                        });
                                        toast.showSuccess(taxHasStatus ? 'Tax status updated' : 'Tax status added');
                                        // refresh customer details and accounts
                                        const data = await getCustomerDetails(customer.customer_number || '');
                                        setCustomer(data);
                                        // trigger accounts reload by calling getCustomerAccounts
                                        const accs = await getCustomerAccounts(data.id, 'positive');
                                        setAccounts(accs);

                                        if (data?.id) {
                                            const taxResp = await getCustomerTaxInfo(data.id);
                                            setTaxHasStatus(!!taxResp.has_status);
                                            setTaxEditable(!!taxResp.editable);
                                            if (taxResp.tax_info) {
                                                setTaxTin(taxResp.tax_info.tin_number || '');
                                                setTaxValidUntil(taxResp.tax_info.valid_until ? String(taxResp.tax_info.valid_until).split('T')[0] : '');
                                                setTaxExistingFile(taxResp.tax_info.file || null);
                                            } else {
                                                setTaxExistingFile(null);
                                            }
                                        }

                                        setTaxDialogOpen(false);
                                    } catch (err: any) {
                                        toast.showError(err?.message ?? 'Failed to add tax status');
                                    } finally {
                                        setTaxSubmitting(false);
                                    }
                                }}
                                loading={taxSubmitting}
                            >
                                {taxHasStatus && taxEditable ? 'Update' : 'Submit'}
                            </Button>
                        )}
                    </Actions>
                </Stack>
            </Dialog>

            <Dialog
                id="customer-qr-dialog"
                title="Download Member QR Code"
                open={qrDialogOpen}
                onClose={() => setQrDialogOpen(false)}
                width="small"
            >
                <Stack space="medium">
                    <TextField
                        label="Card Number (optional)"
                        value={qrCardNumber}
                        onChange={(e: any) => setQrCardNumber(e.currentTarget.value)}
                        placeholder="Leave empty for auto-generated"
                        message="If empty, card number will be customer_number-date"
                    />
                    <Actions>
                        <Button variant="ghost" onClick={() => setQrDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleDownloadQr}>
                            Download
                        </Button>
                    </Actions>
                </Stack>
            </Dialog>
            {/* Transaction Statement Drawer */}
            <Drawer
                title="Customer Transaction Statement"
                open={statementDrawerOpen}
                width="large"
                onClose={() => {
                    setStatementDrawerOpen(false);
                    setStatementData(null);
                }}
            >
                <Box padding="medium">
                    <Stack space="medium">
                        <Box style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <Columns space="medium" alignY="bottom" collapseBelow="tablet">
                                <Column>
                                    <Dropdown
                                        label="Report Type"
                                        value={statementReportType}
                                        onChange={(e) => {
                                            setStatementReportType(e.currentTarget.value as any);
                                            setStatementData(null);
                                        }}
                                    >
                                        <option value="single">Single Account</option>
                                        <option value="all_savings">All Savings</option>
                                        <option value="all_loans">All Loans</option>
                                    </Dropdown>
                                </Column>
                                <Column>
                                    <Dropdown
                                        label="Select PL Account"
                                        value={statementReportType === 'single' ? statementPlAccountId : ''}
                                        onChange={(e) => setStatementPlAccountId(e.currentTarget.value)}
                                        placeholder="Select an account"
                                        disabled={statementReportType !== 'single'}
                                    >
                                        <optgroup label="Saving Accounts Active">
                                            {savingsActiveAccounts.map((acc) => (
                                                <option key={acc.id} value={acc.id?.toString()}>
                                                    {acc.ref_account_number} - {acc.account_type?.name_ln1 || ''}
                                                </option>
                                            )) as any}
                                        </optgroup>
                                        <optgroup label="Loan Accounts Active">
                                            {loanActiveAccounts.map((acc) => (
                                                <option key={acc.id} value={acc.id?.toString()}>
                                                    {acc.ref_account_number} - {acc.account_type?.name_ln1 || ''}
                                                </option>
                                            )) as any}
                                        </optgroup>
                                        <optgroup label="Saving Accounts Inactive">
                                            {savingsClosedAccounts.map((acc) => (
                                                <option key={acc.id} value={acc.id?.toString()}>
                                                    {acc.ref_account_number} - {acc.account_type?.name_ln1 || ''}
                                                </option>
                                            )) as any}
                                        </optgroup>
                                        <optgroup label="Loan Accounts Inactive">
                                            {loanClosedAccounts.map((acc) => (
                                                <option key={acc.id} value={acc.id?.toString()}>
                                                    {acc.ref_account_number} - {acc.account_type?.name_ln1 || ''}
                                                </option>
                                            )) as any}
                                        </optgroup>
                                    </Dropdown>
                                </Column>
                                <Column>
                                    <DateField
                                        label="From Date"
                                        onlyForToday={true}
                                        value={statementFromDate}
                                        onChange={(v) => setStatementFromDate(v || '')}
                                    />
                                </Column>
                                <Column>
                                    <DateField
                                        label="To Date"
                                        onlyForToday={true}
                                        value={statementToDate}
                                        onChange={(v) => setStatementToDate(v || '')}
                                    />
                                </Column>
                            </Columns>
                            <Box display={"flex"} justifyContent="flexEnd" marginTop="medium">
                                <Inline space="small" alignY="center">
                                    <Dropdown
                                        aria-label="Print Language"
                                        value={statementLanguage}
                                        onChange={(e) => setStatementLanguage(e.currentTarget.value as any)}
                                    >
                                        <option value="english">English</option>
                                        <option value="sinhala">Sinhala (සිංහල)</option>
                                        <option value="tamil">Tamil (தமிழ்)</option>
                                    </Dropdown>
                                    <Actions>
                                        <Button
                                            onClick={handleFetchStatement}
                                            loading={statementLoading}
                                            variant="ghost"
                                            tone='formAccent'
                                            icon={<IconDocument />}
                                        >
                                            View Statement
                                        </Button>
                                        {statementData && (
                                            <Button
                                                onClick={handlePrintStatement}
                                                variant="solid"
                                                tone="formAccent"
                                                icon={<IconPrint />}
                                            >
                                                Print Statement
                                            </Button>
                                        )}
                                    </Actions>
                                </Inline>
                            </Box>
                        </Box>

                        {statementLoading && <Loading message="Loading statement details..." />}

                        {!statementLoading && statementData && (
                            <Box
                                padding="medium"
                                borderRadius="standard"
                                style={{
                                    background: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                    color: '#1e293b',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                    overflowX: 'auto',
                                    marginTop: '16px'
                                }}
                            >
                                <style>{`
                                    .statement-preview-table td {
                                        font-size: 13px !important;
                                    }
                                `}</style>
                                <Box style={{ textAlign: 'center', marginBottom: '24px' }}>
                                    <Stack space="small" align="center">
                                        <Text weight="strong" size="large">
                                            {statementLanguage === 'english' ? (
                                                `${statementData.institute_name_ln1 || ''} - Branch: ${statementData.branch_name_ln1 || ''}`
                                            ) : statementLanguage === 'sinhala' ? (
                                                `${getVal(statementData.institute_name_ln2, statementData.institute_name_ln1)} - ශාඛාව : ${getVal(statementData.branch_name_ln2, statementData.branch_name_ln1)}`
                                            ) : (
                                                `${getVal(statementData.institute_name_ln3, statementData.institute_name_ln1)} - கிளை : ${getVal(statementData.branch_name_ln3, statementData.branch_name_ln1)}`
                                            )}
                                        </Text>
                                        <Text weight="medium" size="small" tone="secondary">
                                            {statementLanguage === 'english' ? (
                                                `${statementFromDate} to ${statementToDate} ${statementData.account_category_id === 2 ? 'Loan' : 'Deposit'} Transactions (${statementData.accounts ? (statementData.account_category_id === 2 ? 'All Loan Accounts' : 'All Savings Accounts') : (statementData.account_type_ln1 || '')})`
                                            ) : statementLanguage === 'sinhala' ? (
                                                `${statementFromDate} සිට ${statementToDate} දින දක්වා ${statementData.account_category_id === 2 ? 'ණය' : 'තැන්පත්'} ගනුදෙනු (${statementData.accounts ? (statementData.account_category_id === 2 ? 'සියලුම ණය ගිණුම්' : 'සියලුම තැන්පත් ගිණුම්') : (getVal(statementData.account_type_ln2, statementData.account_type_ln1))})`
                                            ) : (
                                                `${statementFromDate} முதல் ${statementToDate} வரையிலான ${statementData.account_category_id === 2 ? 'கடன்' : 'சேமிப்பு'} பரிவர்த்தனைகள் (${statementData.accounts ? (statementData.account_category_id === 2 ? 'அனைத்து கடன் கணக்குகள்' : 'அனைத்து சேமிப்பு கணக்குகள்') : (getVal(statementData.account_type_ln3, statementData.account_type_ln1))})`
                                            )}
                                        </Text>
                                    </Stack>
                                </Box>

                                <table style={{ width: '100%', marginBottom: '20px', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ padding: '4px 0', fontSize: '14px', color: '#64748b', width: '150px' }}>
                                                {statementLanguage === 'english' ? 'Member No' : statementLanguage === 'sinhala' ? 'සාමා. අංකය' : 'உறுப்பினர் எண்'}
                                            </td>
                                            <td style={{ padding: '4px 0', fontSize: '14px', fontWeight: 'bold' }}>
                                                - {statementData.customer_number}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '4px 0', fontSize: '14px', color: '#64748b' }}>
                                                {statementLanguage === 'english' ? 'Customer Name' : statementLanguage === 'sinhala' ? 'ගනුදෙනුකරුගේ නම' : 'வாடிக்கையாளர் பெயர்'}
                                            </td>
                                            <td style={{ padding: '4px 0', fontSize: '14px', fontWeight: 'bold' }}>
                                                - {statementLanguage === 'english' ? statementData.customer_name_ln1 : statementLanguage === 'sinhala' ? getVal(statementData.customer_name_ln2, statementData.customer_name_ln1) : getVal(statementData.customer_name_ln3, statementData.customer_name_ln1)}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '4px 0', fontSize: '14px', color: '#64748b' }}>
                                                {statementLanguage === 'english' ? 'Customer Address' : statementLanguage === 'sinhala' ? 'ගනුදෙනුකරුගේ ලිපිනය' : 'வாடிக்கையாளர் முகவரி'}
                                            </td>
                                            <td style={{ padding: '4px 0', fontSize: '14px', fontWeight: 'bold' }}>
                                                - {statementLanguage === 'english' ? statementData.address_ln1 : statementLanguage === 'sinhala' ? getVal(statementData.address_ln2, statementData.address_ln1) : getVal(statementData.address_ln3, statementData.address_ln1)}
                                            </td>
                                        </tr>
                                        {!statementData.accounts && (
                                            <tr>
                                                <td style={{ padding: '4px 0', fontSize: '14px', color: '#64748b' }}>
                                                    {statementLanguage === 'english' ? 'Account Number' : statementLanguage === 'sinhala' ? 'ගිණුම් අංකය' : 'கணக்கு எண்'}
                                                </td>
                                                <td style={{ padding: '4px 0', fontSize: '14px', fontWeight: 'bold' }}>
                                                    - {statementData.account_number}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                {statementData.accounts && Array.isArray(statementData.accounts) ? (
                                    statementData.accounts.filter((acc: any) => {
                                        const hasTransactions = acc.transactions && acc.transactions.length > 0;
                                        const isActive = (acc.balance ?? 0) !== 0;
                                        return isActive || hasTransactions;
                                    }).sort((a: any, b: any) => {
                                        const aHasTx = a.transactions && a.transactions.length > 0;
                                        const bHasTx = b.transactions && b.transactions.length > 0;
                                        if (aHasTx && !bHasTx) return -1;
                                        if (!aHasTx && bHasTx) return 1;
                                        return 0;
                                    }).map((acc: any) => {
                                        const accTypeName = statementLanguage === 'english'
                                            ? acc.account_type_ln1
                                            : statementLanguage === 'sinhala'
                                                ? getVal(acc.account_type_ln2, acc.account_type_ln1)
                                                : getVal(acc.account_type_ln3, acc.account_type_ln1);

                                        return (
                                            <Box key={acc.pl_account_id} style={{ marginTop: '24px', marginBottom: '24px' }}>
                                                <div style={{
                                                    fontSize: '14px',
                                                    fontWeight: 'bold',
                                                    padding: '8px 12px',
                                                    background: '#f1f5f9',
                                                    borderLeft: '4px solid #0f172a',
                                                    marginBottom: '12px',
                                                    color: '#0f172a',
                                                    fontFamily: 'Inter, system-ui, sans-serif'
                                                }}>
                                                    {acc.ref_account_number} - {accTypeName}
                                                </div>
                                                <table className="statement-preview-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '2px solid #1e293b', borderTop: '2px solid #1e293b' }}>
                                                            <th style={{ padding: '10px 6px', textAlign: 'left', fontSize: '13px' }}>
                                                                {statementLanguage === 'english' ? 'Transaction No' : statementLanguage === 'sinhala' ? 'ගනුදෙනු අංකය' : 'பரிவர்த்தனை எண்'}
                                                            </th>
                                                            <th style={{ padding: '10px 6px', textAlign: 'left', fontSize: '13px' }}>
                                                                {statementLanguage === 'english' ? 'Date' : statementLanguage === 'sinhala' ? 'දිනය' : 'தேதி'}
                                                            </th>
                                                            <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                                {statementLanguage === 'english' ? 'Debit' : statementLanguage === 'sinhala' ? 'හර' : 'பற்று'}
                                                            </th>
                                                            <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                                {statementLanguage === 'english' ? 'Credit' : statementLanguage === 'sinhala' ? 'බැර' : 'வரவு'}
                                                            </th>
                                                            {statementData.account_category_id === 2 && (
                                                                <>
                                                                    <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                                        {statementLanguage === 'english' ? 'Interest' : statementLanguage === 'sinhala' ? 'පොලිය' : 'வட்டி'}
                                                                    </th>
                                                                    <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                                        {statementLanguage === 'english' ? 'Other' : statementLanguage === 'sinhala' ? 'වෙනත්' : 'இதர'}
                                                                    </th>
                                                                </>
                                                            )}
                                                            <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                                {statementLanguage === 'english' ? 'Balance' : statementLanguage === 'sinhala' ? 'ශේෂය' : 'மீதி'}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr style={{ borderBottom: '1px dotted #cbd5e1', fontStyle: 'italic', color: '#64748b' }}>
                                                            <td colSpan={2} style={{ padding: '8px 6px', fontSize: '13px', fontWeight: 'bold' }}>
                                                                {statementLanguage === 'english' ? 'Opening Balance' : statementLanguage === 'sinhala' ? 'ආරම්භක ශේෂය' : 'ஆரம்ப இருப்பு'}
                                                            </td>
                                                            <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>0.00</td>
                                                            <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>0.00</td>
                                                            {statementData.account_category_id === 2 && (
                                                                <>
                                                                    <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>0.00</td>
                                                                    <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>0.00</td>
                                                                </>
                                                            )}
                                                            <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 'bold', fontSize: '13px', color: '#0f172a' }}>
                                                                {formatCurrency(acc.opening_balance)}
                                                            </td>
                                                        </tr>
                                                        {acc.transactions.map((trans: any) => (
                                                            <tr key={trans.id} style={{ borderBottom: '1px dotted #cbd5e1' }}>
                                                                <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: '13px' }}>{trans.transaction_number || '-'}</td>
                                                                <td style={{ padding: '8px 6px', fontSize: '13px' }}>
                                                                    {formatTransactionDate(trans.entered_date)}
                                                                </td>
                                                                <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px', color: trans.debit > 0 ? '#b91c1c' : 'inherit' }}>
                                                                    {formatCurrency(trans.debit)}
                                                                </td>
                                                                <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px', color: trans.credit > 0 ? '#15803d' : 'inherit' }}>
                                                                    {formatCurrency(trans.credit)}
                                                                </td>
                                                                {statementData.account_category_id === 2 && (
                                                                    <>
                                                                        <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                                            {formatCurrency(trans.interest)}
                                                                        </td>
                                                                        <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                                            {formatCurrency(trans.other)}
                                                                        </td>
                                                                    </>
                                                                )}
                                                                <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 'bold', fontSize: '13px' }}>
                                                                    {formatCurrency(trans.balance)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        <tr style={{ fontWeight: 'bold', borderTop: '1.5px solid #1e293b', borderBottom: '2px double #1e293b' }}>
                                                            <td colSpan={2} style={{ padding: '8px 6px' }}>Total</td>
                                                            <td style={{ padding: '8px 6px', textAlign: 'right' }}>{formatCurrency(acc.transactions.reduce((sum: number, t: any) => sum + (t.debit || 0), 0))}</td>
                                                            <td style={{ padding: '8px 6px', textAlign: 'right' }}>{formatCurrency(acc.transactions.reduce((sum: number, t: any) => sum + (t.credit || 0), 0))}</td>
                                                            {statementData.account_category_id === 2 && (
                                                                <>
                                                                    <td style={{ padding: '8px 6px' }}></td>
                                                                    <td style={{ padding: '8px 6px' }}></td>
                                                                </>
                                                            )}
                                                            <td style={{ padding: '8px 6px' }}></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <table className="statement-preview-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #1e293b', borderTop: '2px solid #1e293b' }}>
                                                <th style={{ padding: '10px 6px', textAlign: 'left', fontSize: '13px' }}>
                                                    {statementLanguage === 'english' ? 'Transaction No' : statementLanguage === 'sinhala' ? 'ගනුදෙනු අංකය' : 'பரிவர்த்தனை எண்'}
                                                </th>
                                                <th style={{ padding: '10px 6px', textAlign: 'left', fontSize: '13px' }}>
                                                    {statementLanguage === 'english' ? 'Date' : statementLanguage === 'sinhala' ? 'දිනය' : 'தேதி'}
                                                </th>
                                                <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                    {statementLanguage === 'english' ? 'Debit' : statementLanguage === 'sinhala' ? 'හර' : 'பற்று'}
                                                </th>
                                                <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                    {statementLanguage === 'english' ? 'Credit' : statementLanguage === 'sinhala' ? 'බැර' : 'வரவு'}
                                                </th>
                                                {statementData.account_category_id === 2 && (
                                                    <>
                                                        <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                            {statementLanguage === 'english' ? 'Interest' : statementLanguage === 'sinhala' ? 'පොලිය' : 'வட்டி'}
                                                        </th>
                                                        <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                            {statementLanguage === 'english' ? 'Other' : statementLanguage === 'sinhala' ? 'වෙනත්' : 'இதர'}
                                                        </th>
                                                    </>
                                                )}
                                                <th style={{ padding: '10px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                    {statementLanguage === 'english' ? 'Balance' : statementLanguage === 'sinhala' ? 'ශේෂය' : 'மீதி'}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr style={{ borderBottom: '1px dotted #cbd5e1', fontStyle: 'italic', color: '#64748b' }}>
                                                <td colSpan={2} style={{ padding: '8px 6px', fontSize: '13px', fontWeight: 'bold' }}>
                                                    {statementLanguage === 'english' ? 'Opening Balance' : statementLanguage === 'sinhala' ? 'ආරම්භක ශේෂය' : 'ஆரம்ப இருப்பு'}
                                                </td>
                                                <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>0.00</td>
                                                <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>0.00</td>
                                                {statementData.account_category_id === 2 && (
                                                    <>
                                                        <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>0.00</td>
                                                        <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>0.00</td>
                                                    </>
                                                )}
                                                <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 'bold', fontSize: '13px', color: '#0f172a' }}>
                                                    {formatCurrency(statementData.opening_balance)}
                                                </td>
                                            </tr>
                                            {statementData.transactions.map((trans: any) => (
                                                <tr key={trans.id} style={{ borderBottom: '1px dotted #cbd5e1' }}>
                                                    <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: '13px' }}>{trans.transaction_number || '-'}</td>
                                                    <td style={{ padding: '8px 6px', fontSize: '13px' }}>
                                                        {formatTransactionDate(trans.entered_date)}
                                                    </td>
                                                    <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px', color: trans.debit > 0 ? '#b91c1c' : 'inherit' }}>
                                                        {formatCurrency(trans.debit)}
                                                    </td>
                                                    <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px', color: trans.credit > 0 ? '#15803d' : 'inherit' }}>
                                                        {formatCurrency(trans.credit)}
                                                    </td>
                                                    {statementData.account_category_id === 2 && (
                                                        <>
                                                            <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                                {formatCurrency(trans.interest)}
                                                            </td>
                                                            <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '13px' }}>
                                                                {formatCurrency(trans.other)}
                                                            </td>
                                                        </>
                                                    )}
                                                    <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 'bold', fontSize: '13px' }}>
                                                        {formatCurrency(trans.balance)}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr style={{ fontWeight: 'bold', borderTop: '1.5px solid #1e293b', borderBottom: '2px double #1e293b' }}>
                                                <td colSpan={2} style={{ padding: '8px 6px' }}>Total</td>
                                                <td style={{ padding: '8px 6px', textAlign: 'right' }}>{formatCurrency(statementData.transactions.reduce((sum: number, t: any) => sum + (t.debit || 0), 0))}</td>
                                                <td style={{ padding: '8px 6px', textAlign: 'right' }}>{formatCurrency(statementData.transactions.reduce((sum: number, t: any) => sum + (t.credit || 0), 0))}</td>
                                                {statementData.account_category_id === 2 && (
                                                    <>
                                                        <td style={{ padding: '8px 6px' }}></td>
                                                        <td style={{ padding: '8px 6px' }}></td>
                                                    </>
                                                )}
                                                <td style={{ padding: '8px 6px' }}></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                )}
                            </Box>
                        )}
                    </Stack>
                </Box>
            </Drawer>

            {/* Customer Search Drawer */}
            <Drawer
                title="Search Customer"
                open={customerSearchDrawerOpen}
                width="large"
                onClose={() => setCustomerSearchDrawerOpen(false)}
            >
                <React.Suspense fallback={<Box padding="large"><Loading /></Box>}>
                    <CustomerSearchDrawer
                        onSelect={(selectedCustomerNumber: string) => {
                            setCustomerSearchDrawerOpen(false);
                            navigate(`/customers/${selectedCustomerNumber}`);
                        }}
                        hideAddButton
                        hideActionColumn
                    />
                </React.Suspense>
            </Drawer>
            {printTxId !== null && (
                <TransactionPrintDrawer
                    transactionId={printTxId}
                    open={printTxId !== null}
                    onClose={() => setPrintTxId(null)}
                    onlyCredits={false}
                    onlyDebits={false}
                    doPrint={false}
                />
            )}
        </Box >
    );
};

export default CustomerProfile;
